import { supabase } from './supabaseClient';
import { apiErrorMessage } from './userFacingErrors';

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

async function fetchWithTimeout(url, options = {}, timeoutMs = 10000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(id);
  }
}

/**
 * Parse API error response and create an enriched Error object
 * @param {Response} response - Fetch response object
 * @param {Object} errorData - Parsed error data from response
 * @returns {Error} Error with additional properties (code, originalPrompt, retryable)
 */
function extractStructuredErrorMessage(payload) {
  if (!payload) return null;
  if (typeof payload === 'string') return payload;
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = extractStructuredErrorMessage(item);
      if (nested) return nested;
    }
    return null;
  }
  if (typeof payload !== 'object') return null;

  return (
    extractStructuredErrorMessage(payload.message) ||
    extractStructuredErrorMessage(payload.msg) ||
    extractStructuredErrorMessage(payload.issues) ||
    extractStructuredErrorMessage(payload.errors) ||
    extractStructuredErrorMessage(payload.detail) ||
    extractStructuredErrorMessage(payload.error) ||
    extractStructuredErrorMessage(payload.error_description) ||
    null
  );
}

function summarizeVerboseErrorMessage(rawMessage) {
  if (typeof rawMessage !== 'string') return null;
  const normalized = rawMessage.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;

  const detailMatcher = normalized.match(
    /(?:^|[\s{,])['"]?(?:msg|message|error_message|detail)['"]?\s*:\s*['"]([^'"]+)['"]/i
  );
  const structuredDetail = detailMatcher?.[1]?.trim() || null;

  const lastErrorIndex = normalized.toLowerCase().indexOf('last error:');
  if (lastErrorIndex !== -1) {
    const prefix = normalized.slice(0, lastErrorIndex).trim().replace(/[.,:;]\s*$/, '');
    if (structuredDetail) {
      return `${prefix}. ${structuredDetail}`.trim();
    }
    return prefix || normalized;
  }

  // If backend accidentally returns a dumped object as string, pull message-like fields.
  if ((normalized.startsWith('{') || normalized.startsWith('[')) && structuredDetail) {
    return structuredDetail;
  }

  return structuredDetail && structuredDetail.length < normalized.length
    ? structuredDetail
    : normalized;
}

function createApiError(response, errorData) {
  const errorNode = errorData?.error || errorData?.detail?.error || null;
  const candidateMessage =
    extractStructuredErrorMessage(errorNode) ||
    extractStructuredErrorMessage(errorData) ||
    null;
  const diagnosticMessage =
    summarizeVerboseErrorMessage(candidateMessage) ||
    `HTTP ${response.status}: ${response.statusText}`;
  const code = errorNode?.code || errorData?.detail?.code || errorData?.code || null;
  const err = new Error(apiErrorMessage(response.status, code));
  err.code = code;
  err.originalPrompt = errorNode?.originalPrompt || null;
  err.retryable = errorNode?.retryable || false;
  err.status = response.status;
  Object.defineProperties(err, {
    raw: { value: errorData, enumerable: false },
    diagnosticMessage: { value: diagnosticMessage, enumerable: false },
  });
  return err;
}

/**
 * Get authentication token from Supabase
 */
async function getAuthToken(options = {}) {
  const { retries = 0, retryDelayMs = 150 } = options || {};
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || null;
    if (token) return token;
    if (attempt < retries) {
      // Small delay for OAuth redirect hydration races
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }
  }
  return null;
}

/**
 * Create a new blank project (start from scratch)
 * @param {string} projectId - Client-generated project ID
 * @returns {Promise<Object>} API response
 */
export async function createBlankProject(projectId) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('project_id', projectId);
  
  console.debug('[createBlankProject] Creating blank project', {
    projectId,
    backendUrl: `${API_BASE_URL}/api/projects/blank`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/blank`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[createBlankProject] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[createBlankProject] request succeeded');
  return response.json();
}

/**
 * Initialize a project at the start of the agent flow.
 * Creates the project record early with 'scoping' status, allowing assets
 * to be saved during the planning/augmentation phase.
 * 
 * @param {string} projectId - Client-generated project ID
 * @param {string} prompt - User's initial prompt
 * @returns {Promise<Object>} API response with projectId and status
 */
export async function initializeProject(projectId, prompt, aspectRatio) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('prompt', prompt);
  formData.append('aspect_ratio', aspectRatio || '16:9');
  
  console.debug('[initializeProject] Initializing project', {
    projectId,
    promptLength: prompt?.length || 0,
    backendUrl: `${API_BASE_URL}/api/projects/init`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/init`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[initializeProject] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[initializeProject] request succeeded');
  return response.json();
}


/**
 * Submit post-signup onboarding questionnaire answers
 * This should be non-blocking relative to project generation.
 * @param {Object} payload
 * @param {'marketing'|'personal'} [payload.useType]
 * @param {string} [payload.intendedUse]
 * @param {string} [payload.maxVideoLength]
 * @param {string} [payload.company]
 * @param {string} [payload.role]
 * @param {string} [payload.heardAboutUs]
 * @param {string} [payload.productWebsiteUrl]
 */
export async function submitOnboarding(payload) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/onboarding`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  }, 10000);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get onboarding completion status for current user
 * @returns {Promise<{ success: boolean, completed: boolean, useType: 'marketing'|'personal'|null, productWebsiteUrl?: string|null, needsProductWebsiteUrl?: boolean }>}
 */
export async function getOnboardingStatus() {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/onboarding/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Start the marketing website scan for onboarding
 * @param {Object} payload
 * @param {string} [payload.productWebsiteUrl]
 */
export async function startMarketingWebsiteScan(payload) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/onboarding/marketing/scan`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  }, 15000);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get the current marketing website scan status
 * @returns {Promise<{ success: boolean, status: string, productDescription?: string, productImageUrls?: string[], productSummaryJson?: Record<string, any> }>}
 */
export async function getMarketingWebsiteScanStatus() {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/onboarding/marketing/scan/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Save user edits to marketing onboarding scan results
 * @param {Object} payload
 * @param {string} [payload.productDescription]
 * @param {string[]} [payload.productImageUrls]
 */
export async function updateMarketingWebsiteScan(payload) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/onboarding/marketing/scan`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  }, 15000);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Upload a product image for marketing onboarding
 * @param {File} file
 */
export async function uploadMarketingWebsiteImage(file) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/onboarding/marketing/images`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  }, 30000);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Save selected character for marketing onboarding.
 * @param {Object} payload
 * @param {string|null} [payload.selectedCharacterId]
 */
export async function updateMarketingCharacterSelection(payload) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetchWithTimeout(`${API_BASE_URL}/api/onboarding/marketing/character`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  }, 15000);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get storyboard guide status for current user
 * @returns {Promise<{ success: boolean, hasSeenStoryboardGuide: boolean }>}
 */
export async function getStoryboardGuideStatus() {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/guides/storyboard/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Mark storyboard guide as seen for current user
 * @returns {Promise<{ success: boolean }>}
 */
export async function markStoryboardGuideSeen() {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/guides/storyboard/seen`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get project details by ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} Project data
 */
export async function getProject(projectId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get lightweight project preview (no auth required).
 * Sends auth token if available so the backend can determine ownership.
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} Preview data with stats and isOwner flag
 */
export async function getProjectPreview(projectId) {
  const token = await getAuthToken();

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/preview`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Fork (deep-copy) a project to the current user's account.
 * Requires authentication.
 * @param {string} projectId - Source project ID
 * @returns {Promise<Object>} { success, projectId: newProjectId }
 */
export async function forkProject(projectId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/fork`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete a project permanently
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { success: true }
 */
export async function deleteProject(projectId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update project name
 * @param {string} projectId - Project ID
 * @param {string} name - New project name
 * @returns {Promise<Object>} Updated project
 */
export async function updateProjectName(projectId, name) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update project configuration (style, aspect ratio)
 * @param {string} projectId - Project ID
 * @param {Object} config - Configuration updates
 * @param {string} config.style - Project style
 * @param {string} config.aspectRatio - Aspect ratio (16:9, 9:16, 1:1, 4:3)
 * @returns {Promise<Object>} Updated project
 */
export async function updateProjectConfig(projectId, config) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateProjectConfig] Updating project config', {
    projectId,
    config,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}`
  });

  try {
    const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const text = await response.clone().text().catch(() => null);
      const error = await response.json().catch(() => ({}));
      console.error('[updateProjectConfig] request failed', {
        status: response.status,
        statusText: response.statusText,
        responseText: text,
        parsedError: error
      });
      throw createApiError(response, error);
    }

    console.debug('[updateProjectConfig] request succeeded');
    return response.json();
  } catch (err) {
    // Network error or fetch failed
    console.error('[updateProjectConfig] Network error', {
      error: err.message,
      projectId,
      config,
      apiBaseUrl: API_BASE_URL
    });
    throw err;
  }
}

/**
 * Export project as video
 * @param {string} projectId - Project ID
 * @param {Object} options - Export options
 * @returns {Promise<Object>} Export job details
 */
export async function exportProject(projectId, options = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/export`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      format: options.format || 'mp4',
      quality: options.quality || 'high',
      includeAudio: options.includeAudio !== false,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get export status
 * @param {string} projectId - Project ID
 * @param {string} exportId - Export job ID
 * @returns {Promise<Object>} Export status
 */
export async function getExportStatus(projectId, exportId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/exports/${exportId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Check user entitlements/subscription status
 * @param {string} appUserId - Supabase user ID
 * @returns {Promise<Object>} Entitlement status
 */
export async function checkEntitlements(appUserId) {
  const response = await fetch(`${API_BASE_URL}/api/entitlements?appUserId=${appUserId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Create Stripe checkout session for metered billing
 * @param {string} customerEmail - User's email
 * @param {string} successUrl - URL to redirect on successful checkout
 * @param {string} cancelUrl - URL to redirect on cancelled checkout
 * @returns {Promise<Object>} Checkout session URL and ID
 */
export async function createCheckoutSession(customerEmail, successUrl, cancelUrl) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/checkout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      customerEmail,
      successUrl,
      cancelUrl,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Setup billing for the current user
 * Creates Stripe customer, RevenueCat user, and returns checkout URL
 * @param {string} successUrl - URL to redirect on successful checkout
 * @param {string} cancelUrl - URL to redirect on cancelled checkout
 * @returns {Promise<Object>} Customer ID and checkout URL
 */
export async function setupBilling(successUrl, cancelUrl) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/billing/setup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      successUrl,
      cancelUrl,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get Stripe billing portal URL
 * @param {string} returnUrl - URL to return to after leaving the portal
 * @returns {Promise<Object>} Billing portal URL
 */
export async function getBillingPortal(returnUrl) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/billing/portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      returnUrl,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get the current organization's Stripe-backed payment methods and permissions.
 */
export async function getPaymentMethods() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/payment-methods`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Start Stripe Checkout in setup mode for a new organization card. */
export async function createPaymentMethodSetup(returnUrl) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/payment-methods/setup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ returnUrl }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Confirm a completed Stripe setup-mode Checkout session. */
export async function confirmPaymentMethodSetup(checkoutSessionId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/payment-methods/confirm`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ checkoutSessionId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Make an attached organization card the default for Stripe invoices. */
export async function setDefaultPaymentMethod(paymentMethodId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(
    `${API_BASE_URL}/api/billing/payment-methods/${encodeURIComponent(paymentMethodId)}/default`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Remove a non-default organization card from Stripe. */
export async function removePaymentMethod(paymentMethodId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(
    `${API_BASE_URL}/api/billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Get current billing period usage
 * @returns {Promise<Object>} Usage statistics for current billing period
 */
export async function getBillingUsage() {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/billing/usage`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get daily usage breakdown for current billing period
 * @returns {Promise<Object>} Daily usage data
 */
export async function getDailyBillingUsage() {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/billing/usage/daily`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get credit balance for current user
 */
export async function getCreditBalance() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/credits`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Redeem a coupon code
 */
export async function redeemCoupon(code) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/redeem-coupon`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

// ============================================================================
// Credit Purchase API
// ============================================================================

/**
 * Purchase credits via Stripe Checkout
 * @param {number} amountCents - Amount in cents (min 500, max 10000000)
 * @param {string} successUrl - Redirect URL on success
 * @param {string} cancelUrl - Redirect URL on cancel
 * @param {{ eventId?: string, fbc?: string, fbp?: string }} [attribution] - Ad attribution data
 * @returns {Promise<Object>} { checkoutUrl, checkoutSessionId }
 */
export async function purchaseCredits(amountCents, successUrl, cancelUrl, attribution) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const body = { amountCents, successUrl, cancelUrl };
  if (attribution?.eventId) body.eventId = attribution.eventId;
  if (attribution?.fbc) body.fbc = attribution.fbc;
  if (attribution?.fbp) body.fbp = attribution.fbp;

  const response = await fetch(`${API_BASE_URL}/api/billing/credits/purchase`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Confirm a completed Stripe Checkout credit purchase.
 * Useful as a fallback when webhook delivery is delayed/unavailable.
 * @param {string} checkoutSessionId - Stripe Checkout session id
 * @returns {Promise<Object>} { applied, alreadyProcessed, creditedCents, newBalanceCents, newBalanceDollars }
 */
export async function confirmCreditPurchase(checkoutSessionId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/credits/confirm`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ checkoutSessionId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get credit settings (billing mode, auto-reload config)
 * @returns {Promise<Object>} { billingMode, autoReloadEnabled, autoReloadThresholdCents, autoReloadAmountCents, ... }
 */
export async function getCreditSettings() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/credits/settings`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update auto-reload settings
 * @param {Object} settings - { autoReloadEnabled, autoReloadThresholdCents, autoReloadAmountCents }
 * @returns {Promise<Object>} Updated settings
 */
export async function updateCreditSettings(settings) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/credits/settings`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Switch from PAYG to credit-based billing
 * @returns {Promise<Object>} { billingMode, bonusCents, newBalanceCents, newBalanceDollars }
 */
export async function switchToCredits() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/credits/switch`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get credit purchase history
 * @param {number} [limit=50] - Max items to return
 * @param {number} [offset=0] - Offset for pagination
 * @returns {Promise<Object>} { items: [...], total: number }
 */
export async function getCreditHistory(limit = 50, offset = 0) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(
    `${API_BASE_URL}/api/billing/credits/history?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}


// ---------------------------------------------------------------------------
// Subscription Management
// ---------------------------------------------------------------------------

/**
 * Create a subscription checkout session
 * @param {'creator'|'studio'} tier
 * @param {string} successUrl
 * @param {string} cancelUrl
 * @param {{ eventId?: string, fbc?: string, fbp?: string }} [attribution] - Ad attribution data
 */
export async function subscribe(tier, successUrl, cancelUrl, attribution) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const body = { tier, successUrl, cancelUrl };
  if (attribution?.eventId) body.eventId = attribution.eventId;
  if (attribution?.fbc) body.fbc = attribution.fbc;
  if (attribution?.fbp) body.fbp = attribution.fbp;

  const response = await fetch(`${API_BASE_URL}/api/billing/subscribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Send ad attribution data (fbclid, UTM params) to the backend for CAPI tracking.
 * @param {{ fbclid?: string, fbc?: string, fbp?: string, utmSource?: string, utmMedium?: string, utmCampaign?: string, utmContent?: string, utmTerm?: string }} data
 */
export async function sendAdAttribution(data) {
  const token = await getAuthToken();
  if (!token) return;

  try {
    await fetch(`${API_BASE_URL}/api/attribution`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch {
    // Fire-and-forget: attribution failures should never block the user
  }
}

/**
 * Send a server-side event to Meta CAPI via the backend.
 * Fire-and-forget — failures never block the user.
 * @param {string} eventName - Meta event name (e.g., 'CompleteRegistration', 'InitiateCheckout')
 * @param {string} eventId - Event ID for dedup with client-side Pixel
 * @param {Object} [eventData] - Custom data (value, currency, etc.)
 * @param {{ fbc?: string, fbp?: string }} [attribution] - Attribution cookies
 */
export async function sendCAPIEvent(eventName, eventId, eventData = {}, attribution = {}) {
  const token = await getAuthToken();
  if (!token) return;

  try {
    await fetch(`${API_BASE_URL}/api/attribution/event`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName,
        eventId,
        eventData,
        fbc: attribution.fbc,
        fbp: attribution.fbp,
        eventSourceUrl: window.location.href,
      }),
    });
  } catch {
    // Fire-and-forget
  }
}

/**
 * Get current subscription details
 */
export async function getSubscription() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/subscription`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Cancel subscription at end of current period
 */
export async function cancelSubscription() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/subscription/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Confirm subscription activation after returning from Stripe checkout.
 * Idempotent — safe to call even if webhook already processed it.
 * @param {string} sessionId - Stripe checkout session ID
 */
export async function confirmSubscription(sessionId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/subscription/confirm`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Change subscription tier (upgrade/downgrade)
 * @param {'creator'|'studio'} newTier
 */
export async function changePlan(newTier) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/billing/subscription/change-plan`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newTier }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get organization info for current user
 */
export async function getMyOrg() {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/orgs/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get notification contact info for the current user.
 */
export async function getMyNotificationContact() {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/users/me/notification-contact`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update notification contact phone number for the current user.
 * @param {{ phoneNumber: string }} payload
 */
export async function updateMyNotificationContact(payload) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/users/me/notification-contact`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update organization settings (name, etc.)
 * @param {Object} payload - Update payload
 * @param {string} [payload.name] - New organization name
 */
export async function updateOrg(payload) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/orgs/me`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * List organization members and invites
 */
export async function getOrgMembers() {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/orgs/members`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Create an organization invite
 */
export async function createOrgInvite(payload) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/orgs/invites`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Accept an organization invite
 */
export async function acceptOrgInvite(payload) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/orgs/invites/accept`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Validate org invite token
 */
export async function validateOrgInvite(token) {
  if (!token) {
    throw new Error('Invite token required');
  }

  let authToken = null;
  try {
    authToken = await getAuthToken();
  } catch {
    // Invite validation remains public if local auth hydration is unavailable.
  }
  const headers = { 'Content-Type': 'application/json' };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const response = await fetch(`${API_BASE_URL}/api/orgs/invites/validate?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Accept org invite without authentication (creates user)
 */
export async function acceptOrgInvitePublic(payload) {
  const response = await fetch(`${API_BASE_URL}/api/orgs/invites/accept-public`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/** Resend a pending or expired organization invitation. */
export async function resendOrgInvite(inviteId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/orgs/invites/${encodeURIComponent(inviteId)}/resend`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Revoke an organization invitation. */
export async function revokeOrgInvite(inviteId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/orgs/invites/${encodeURIComponent(inviteId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Remove a non-owner member from the current organization. */
export async function removeOrgMember(userId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/orgs/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * List projects for the current user with pagination
 * @param {Object} options - Pagination options
 * @param {number} options.page - Page number (1-indexed, default 1)
 * @param {number} options.limit - Items per page (default 12)
 * @param {string} options.status - Filter by status (completed, processing, failed)
 * @param {string} options.search - Search by project name
 * @param {boolean} options.activityOnly - Return lightweight run activity metadata
 * @returns {Promise<Object>} { projects, pagination: { page, limit, totalCount, totalPages, hasNext, hasPrev } }
 */
export async function listProjects({ page = 1, limit = 12, status = null, search = null, activityOnly = false } = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Build URL with query params
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  if (status && status !== 'all') {
    params.append('status_filter', status);
  }
  if (search && search.trim()) {
    params.append('search', search.trim());
  }
  if (activityOnly) {
    params.append('activity_only', 'true');
  }
  const response = await fetch(`${API_BASE_URL}/api/projects?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Discover trending short videos across platforms, optionally filtered by a keyword.
 * Leave `keyword` empty to get whatever is currently trending.
 * `datePosted` (recency) and `cursor` (pagination) only apply to keyword searches;
 * `cursor` is a platform-native opaque token and is unsupported when platform='all'.
 * `region` is a 2-letter country code honoured by TikTok/YouTube only.
 * @param {string} keyword - Optional search term (empty = trending)
 * @param {{ limit?: number, platform?: 'all'|'instagram'|'tiktok'|'youtube', datePosted?: string|null, region?: string|null, cursor?: string|null }} options
 * @returns {Promise<{ reels: Array, platform: string, keyword: string|null, next_cursor: string|null, has_more: boolean, credits_remaining: number|null }>}
 */
/**
 * Same-origin proxy URL for a reel thumbnail/poster image. Third-party social
 * CDN images can be blocked client-side (tracking protection, CSP, referer);
 * routing them through our backend serves them from our own origin so they load
 * reliably. Returns the input unchanged for non-string values.
 */
export function reelMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  return `${API_BASE_URL}/api/reels/media?url=${encodeURIComponent(url)}`;
}

export async function searchTrendingReels(
  keyword = '',
  { limit = 30, platform = 'all', datePosted = null, region = null, cursor = null } = {},
) {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('platform', platform || 'all');
  if (keyword && keyword.trim()) {
    params.append('keyword', keyword.trim());
  }
  if (datePosted) {
    params.append('date_posted', datePosted);
  }
  if (region) {
    params.append('region', region);
  }
  if (cursor) {
    params.append('cursor', cursor);
  }

  const response = await fetch(`${API_BASE_URL}/api/reels/trending?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Stream a Gemini "why it works" analysis for a reel.
 * Calls `onToken(textChunk)` for each streamed chunk; resolves when the stream
 * ends. Pass an AbortSignal to cancel (e.g. when the modal closes).
 * @param {Object} reel - normalized reel object
 * @param {{ onToken?: (chunk: string) => void, signal?: AbortSignal }} options
 */
export async function analyzeReelStream(reel, { onToken, signal } = {}) {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/reels/analyze`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      platform: reel.platform,
      video_url: reel.video_url,
      permalink: reel.permalink,
      shortcode: reel.shortcode,
      caption: reel.caption,
      username: reel.username,
      thumbnail_url: reel.thumbnail_url,
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const text = decoder.decode(value, { stream: true });
    if (text && onToken) onToken(text);
  }
}

/**
 * Generate an asset for a project
 * @param {string} projectId - Project ID
 * @param {string} prompt - Text description of the asset
 * @param {File[]} images - Optional array of reference images
 * @param {string} name - Optional name for the asset
 * @param {Object} options
 * @param {string[]} [options.referenceImageUrls] - Array of existing asset image URLs
 * @param {string|null} [options.model] - Optional model override
 * @returns {Promise<Object>} Generated asset data
 */
export async function generateAsset(projectId, prompt, images = [], name = null, options = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    model = null,
    referenceImageUrls = null,
    aspectRatio = null,
    outputFormat = null,
    seed,
    guidanceScale,
    numInferenceSteps,
    acceleration,
    enablePromptExpansion,
    enableSafetyChecker,
    negativePrompt,
    resolution,
    limitGenerations,
    enableWebSearch,
    quality,
  } = options || {};

  const formData = new FormData();
  formData.append('prompt', prompt);
  if (name) {
    formData.append('name', name);
  }
  if (model) {
    formData.append('model', model);
  }
  if (aspectRatio) formData.append('aspect_ratio', aspectRatio);
  if (outputFormat) formData.append('output_format', outputFormat);
  if (seed !== undefined && seed !== null && seed !== '') formData.append('seed', String(seed));
  if (guidanceScale !== undefined && guidanceScale !== null && guidanceScale !== '') formData.append('guidance_scale', String(guidanceScale));
  if (numInferenceSteps !== undefined && numInferenceSteps !== null && numInferenceSteps !== '') formData.append('num_inference_steps', String(numInferenceSteps));
  if (acceleration) formData.append('acceleration', acceleration);
  if (enablePromptExpansion !== undefined) formData.append('enable_prompt_expansion', String(enablePromptExpansion));
  if (enableSafetyChecker !== undefined) formData.append('enable_safety_checker', String(enableSafetyChecker));
  if (negativePrompt) formData.append('negative_prompt', negativePrompt);
  if (resolution) formData.append('resolution', resolution);
  if (limitGenerations !== undefined) formData.append('limit_generations', String(limitGenerations));
  if (enableWebSearch !== undefined) formData.append('enable_web_search', String(enableWebSearch));
  if (quality) formData.append('quality', quality);
  
  // Append image files
  images.forEach((image) => {
    formData.append('files', image);
  });
  if (referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
    formData.append('reference_image_urls', JSON.stringify(referenceImageUrls));
  }

  console.debug('[generateAsset] Generating asset', {
    projectId,
    promptLength: prompt?.length,
    imageCount: images.length,
    referenceImageUrlCount: referenceImageUrls?.length || 0,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets/generate`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/generate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[generateAsset] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[generateAsset] request succeeded');
  return response.json();
}

/**
 * Upload an asset image for a project
 * @param {string} projectId - Project ID
 * @param {File} imageFile - Image file to upload
 * @returns {Promise<Object>} Uploaded asset data with AI-generated name and description
 */
export async function uploadAsset(projectId, imageFile) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('file', imageFile);

  console.debug('[uploadAsset] Uploading asset', {
    projectId,
    fileName: imageFile.name,
    fileSize: imageFile.size,
    fileType: imageFile.type,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets/upload`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[uploadAsset] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[uploadAsset] request succeeded');
  return response.json();
}

/**
 * Upload an image file for element use (returns CDN URL)
 * This is a lightweight wrapper around uploadAsset that returns just the imageUrl
 * @param {string} projectId - Project ID
 * @param {File} imageFile - Image file to upload
 * @returns {Promise<string>} The uploaded image URL
 */
export async function uploadElementImage(projectId, imageFile) {
  console.debug('[uploadElementImage] Uploading element image', {
    projectId,
    fileName: imageFile.name,
    fileSize: imageFile.size,
  });

  const result = await uploadAsset(projectId, imageFile);
  console.debug('[uploadElementImage] uploadAsset returned:', result);
  
  // Try multiple possible field names from the response
  const imageUrl = result?.imageUrl || result?.image_url || result?.asset?.imageUrl || result?.asset?.image_url;
  
  if (!imageUrl) {
    console.error('[uploadElementImage] No image URL found in response:', result);
    throw new Error('Upload succeeded but no image URL returned');
  }
  
  console.debug('[uploadElementImage] Upload succeeded', { imageUrl });
  return imageUrl;
}

/** Upload an image for a standalone /videos generation without creating an asset or project. */
export async function uploadStandaloneImage(imageFile) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('image_file', imageFile);
  const response = await fetch(`${API_BASE_URL}/api/videos/upload-reference-image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  const result = await response.json();
  const imageUrl = result?.image_url || result?.imageUrl || result?.url;
  if (!imageUrl) throw new Error('Upload succeeded but no image URL returned');
  return imageUrl;
}

/**
 * Generate an image with AI for element use
 * This is a lightweight wrapper around generateAsset that returns just the imageUrl
 * @param {string} projectId - Project ID
 * @param {string} prompt - Text description of the image to generate
 * @param {Object} options
 * @param {string|null} [options.model] - Optional model override
 * @returns {Promise<string>} The generated image URL
 */
export async function generateElementImage(projectId, prompt, options = {}) {
  console.debug('[generateElementImage] Generating element image', {
    projectId,
    promptLength: prompt?.length,
  });

  const { model = null } = options || {};
  const result = await generateAsset(projectId, prompt, [], null, { model });
  console.debug('[generateElementImage] generateAsset returned:', result);
  
  // Try multiple possible field names from the response (including nested asset object)
  const imageUrl = result?.imageUrl || result?.image_url || result?.asset?.imageUrl || result?.asset?.image_url;
  
  if (!imageUrl) {
    console.error('[generateElementImage] No image URL found in response:', result);
    throw new Error('Generation succeeded but no image URL returned');
  }
  
  console.debug('[generateElementImage] Generation succeeded', { imageUrl });
  return imageUrl;
}

/**
 * Get all assets for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} List of assets for the project
 */
export async function getProjectAssets(projectId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[getProjectAssets] Fetching assets', {
    projectId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[getProjectAssets] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[getProjectAssets] request succeeded');
  return response.json();
}

/**
 * Wrap an existing AudioClip into an audio-typed Asset.
 * The clip's role is flipped to 'library' so it leaves the timeline.
 */
export async function assetFromAudioClip(projectId, audioClipId, name = null) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const formData = new FormData();
  if (name) formData.append('name', name);
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/assets/from-audio-clip/${audioClipId}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Wrap an existing Video into a video-typed Asset (flips role to 'library').
 */
export async function assetFromVideo(projectId, videoId, name = null) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const formData = new FormData();
  if (name) formData.append('name', name);
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/assets/from-video/${videoId}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Duplicate an asset's backing media for use as an isolated video input.
 */
export async function createAssetInputCopy(projectId, assetId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  if (!projectId) throw new Error('Project ID is required');
  if (!assetId) throw new Error('Asset ID is required');

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/input-copy`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Update an existing asset with new generation
 * @param {string} projectId - Project ID
 * @param {string} assetId - Asset ID to update
 * @param {string} prompt - New text description
 * @param {File[]} images - Optional array of reference images
 * @param {Object} options - Optional parameters
 * @param {boolean} options.preview - If true, generates image without persisting to DB
 * @param {string} options.commitUrl - If provided, commits this URL without regenerating
 * @param {boolean} options.commitUpload - If true, commits the first image upload without regenerating
 * @returns {Promise<Object>} Updated asset data
 */
export async function updateAsset(projectId, assetId, prompt, images = [], options = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const { preview = false, commitUrl = null, commitUpload = false, referenceImageUrls = null, model = null } = options || {};

  const formData = new FormData();
  formData.append('prompt', prompt);
  if (model) {
    formData.append('model', model);
  }
  
  // Append image files
  images.forEach((image) => {
    formData.append('files', image);
  });

  if (preview) {
    formData.append('preview', 'true');
  }
  if (commitUrl) {
    formData.append('commit_url', commitUrl);
  }
  if (commitUpload) {
    formData.append('commit_upload', 'true');
  }
  if (referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
    formData.append('reference_image_urls', JSON.stringify(referenceImageUrls));
  }

  console.debug('[updateAsset] Updating asset', {
    projectId,
    assetId,
    promptLength: prompt?.length,
    imageCount: images.length,
    preview,
    commitUrl: commitUrl ? '(provided)' : null,
    commitUpload,
    referenceImageUrlCount: Array.isArray(referenceImageUrls) ? referenceImageUrls.length : 0,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[updateAsset] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[updateAsset] request succeeded');
  return response.json();
}

export async function restoreProjectAssetVersion(projectId, assetId, versionNumber) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/restore-version`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ versionNumber }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function changeProjectAssetCameraAngle(projectId, assetId, params = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const {
    horizontalAngle = 0,
    verticalAngle = 0,
    zoom = 5,
    preview = true,
    commitUrl = null,
    model = null,
  } = params;

  const formData = new FormData();
  formData.append('horizontal_angle', String(horizontalAngle));
  formData.append('vertical_angle', String(verticalAngle));
  formData.append('zoom', String(zoom));
  formData.append('preview', String(preview));
  if (commitUrl) formData.append('commit_url', commitUrl);
  if (model) formData.append('model', model);

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/camera-angle`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

async function appendMagicSelectImage(formData, imageUrl, filename = 'asset-image.png') {
  if (!imageUrl) return false;
  if (imageUrl.startsWith('blob:')) {
    const blobResponse = await fetch(imageUrl);
    const blob = await blobResponse.blob();
    const file = new File([blob], filename, { type: blob.type || 'image/png' });
    formData.append('image', file);
    return true;
  }
  formData.append('image_url', imageUrl);
  return false;
}

export async function magicSelectProjectAsset(projectId, assetId, box, imageUrl = null) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('box_x1', String(box.x1));
  formData.append('box_y1', String(box.y1));
  formData.append('box_x2', String(box.x2));
  formData.append('box_y2', String(box.y2));
  await appendMagicSelectImage(formData, imageUrl, 'project-asset-image.png');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/magic-select`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Update asset name
 * @param {string} projectId - Project ID
 * @param {string} assetId - Asset ID to update
 * @param {string} name - New name for the asset
 * @returns {Promise<Object>} Updated asset data
 */
export async function updateAssetName(projectId, assetId, name) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateAssetName] Updating asset name', {
    projectId,
    assetId,
    name,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/name`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/name`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[updateAssetName] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[updateAssetName] request succeeded');
  return response.json();
}

/**
 * Delete an asset from a project
 * @param {string} projectId - Project ID
 * @param {string} assetId - Asset ID (UUID)
 * @returns {Promise<Object>} Success response
 */
export async function deleteAsset(projectId, assetId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[deleteAsset] Deleting asset', {
    projectId,
    assetId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}`,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[deleteAsset] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[deleteAsset] request succeeded');
  return response.json();
}

/**
 * Generate multi-angle views (90° and 180°) for an existing asset.
 * This enables the asset to be used as a consistent character/object reference
 * in Kling O1 reference-to-video generation.
 * 
 * @param {string} projectId - Project ID
 * @param {string} assetId - Asset ID (UUID)
 * @returns {Promise<Object>} Updated asset with secondaryAngles populated
 */
export async function generateAssetAngles(projectId, assetId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[generateAssetAngles] Generating angles for asset', {
    projectId,
    assetId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/generate-angles`,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/${assetId}/generate-angles`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateAssetAngles] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateAssetAngles] request succeeded');
  return response.json();
}

// ============================================================================
// Organization-Level Asset API
// ============================================================================

/**
 * List top-level org assets for the authenticated user/org.
 * Omitting options preserves the existing unpaginated behavior.
 */
export async function getOrgAssets({
  search,
  limit,
  offset,
  mediaType,
} = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  console.debug('[getOrgAssets] fetching');
  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  if (Number.isFinite(limit)) params.set('limit', String(limit));
  if (Number.isFinite(offset)) params.set('offset', String(offset));
  if (mediaType) params.set('media_type', mediaType);
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/api/org-assets/${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Get a single org asset with its sub-assets.
 * @param {string} assetId
 */
export async function getOrgAsset(assetId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  console.debug('[getOrgAsset]', assetId);
  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Generate an org-level asset with AI.
 * @param {string} prompt
 * @param {File[]} images - optional reference images
 * @param {string|null} name
 * @param {Object} options - { parentAssetId?, model?, referenceImageUrls?, aspectRatio? }
 */
export async function generateOrgAsset(prompt, images = [], name = null, options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('prompt', prompt);
  if (name) formData.append('name', name);
  if (options.model) formData.append('model', options.model);
  if (options.aspectRatio) formData.append('aspect_ratio', options.aspectRatio);
  if (options.parentAssetId) formData.append('parent_asset_id', options.parentAssetId);
  if (options.referenceImageUrls?.length) {
    formData.append('reference_image_urls', JSON.stringify(options.referenceImageUrls));
  }
  images.forEach((img) => formData.append('files', img));

  console.debug('[generateOrgAsset]', { prompt: prompt.slice(0, 60) });
  const response = await fetch(`${API_BASE_URL}/api/org-assets/generate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Upload an image file as an org-level asset.
 * @param {File} imageFile
 * @param {Object} options - { parentAssetId? }
 */
export async function uploadOrgAsset(imageFile, options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('file', imageFile);
  if (options.parentAssetId) formData.append('parent_asset_id', options.parentAssetId);

  console.debug('[uploadOrgAsset]', imageFile.name);
  const response = await fetch(`${API_BASE_URL}/api/org-assets/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Generate multi-angle views (90°/180°/270°) for an org/global asset.
 * Mirrors generateAssetAngles() but for the org-asset scope (no project required).
 *
 * @param {string} assetId - Org asset ID
 * @returns {Promise<Object>} { success, asset } with secondaryAngles populated
 */
export async function generateOrgAssetAngles(assetId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[generateOrgAssetAngles]', assetId);
  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}/generate-angles`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateOrgAssetAngles] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * AI re-generation of an org asset image.
 * @param {string} assetId
 * @param {string} prompt
 * @param {File[]} images
 * @param {Object} options - { preview?, commitUrl?, commitUpload?, model?, referenceImageUrls? }
 */
export async function updateOrgAsset(assetId, prompt, images = [], options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('prompt', prompt);
  if (options.preview) formData.append('preview', 'true');
  if (options.commitUrl) formData.append('commit_url', options.commitUrl);
  if (options.commitUpload) formData.append('commit_upload', 'true');
  if (options.model) formData.append('model', options.model);
  if (options.referenceImageUrls?.length) {
    formData.append('reference_image_urls', JSON.stringify(options.referenceImageUrls));
  }
  images.forEach((img) => formData.append('files', img));

  console.debug('[updateOrgAsset]', assetId);
  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function restoreOrgAssetVersion(assetId, versionNumber) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}/restore-version`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ versionNumber }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function changeOrgAssetCameraAngle(assetId, params = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const {
    horizontalAngle = 0,
    verticalAngle = 0,
    zoom = 5,
    preview = true,
    commitUrl = null,
    model = null,
  } = params;

  const formData = new FormData();
  formData.append('horizontal_angle', String(horizontalAngle));
  formData.append('vertical_angle', String(verticalAngle));
  formData.append('zoom', String(zoom));
  formData.append('preview', String(preview));
  if (commitUrl) formData.append('commit_url', commitUrl);
  if (model) formData.append('model', model);

  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}/camera-angle`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function magicSelectOrgAsset(assetId, box, imageUrl = null) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('box_x1', String(box.x1));
  formData.append('box_y1', String(box.y1));
  formData.append('box_x2', String(box.x2));
  formData.append('box_y2', String(box.y2));
  await appendMagicSelectImage(formData, imageUrl, 'org-asset-image.png');

  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}/magic-select`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Update name and/or prompt of an org asset (no image change).
 * @param {string} assetId
 * @param {{ name?: string, prompt?: string }} updates
 */
export async function patchOrgAsset(assetId, updates) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[patchOrgAsset]', assetId, updates);
  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Delete an org asset (sub-assets cascade automatically).
 * @param {string} assetId
 */
export async function deleteOrgAsset(assetId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[deleteOrgAsset]', assetId);
  const response = await fetch(`${API_BASE_URL}/api/org-assets/${assetId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Copy an org asset into a project's asset library.
 * @param {string} projectId
 * @param {string} orgAssetId
 */
export async function importOrgAssetToProject(projectId, orgAssetId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[importOrgAssetToProject]', { projectId, orgAssetId });
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/assets/import-from-org`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ orgAssetId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Characters
// ---------------------------------------------------------------------------

export async function getCharacters({ search, limit, offset } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  if (Number.isFinite(limit)) params.set('limit', String(limit));
  if (Number.isFinite(offset)) params.set('offset', String(offset));
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/api/characters/${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getLibraryCharacters({ search, limit, offset } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  if (Number.isFinite(limit)) params.set('limit', String(limit));
  if (Number.isFinite(offset)) params.set('offset', String(offset));
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/api/characters/library${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getCharacter(characterId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/characters/${characterId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Spaces
// ---------------------------------------------------------------------------

export async function listSpaces({ search = null } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const params = new URLSearchParams();
  if (search?.trim()) params.set('search', search.trim());
  const suffix = params.toString() ? `?${params}` : '';
  const response = await fetch(`${API_BASE_URL}/api/spaces${suffix}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function createSpace(payload) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getSpace(spaceId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getSpaceNodes(spaceId, { parentId = null, cursor = null, limit = 100 } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const params = new URLSearchParams({ limit: String(limit) });
  if (parentId) params.set('parent_id', parentId);
  if (cursor) params.set('cursor', cursor);
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getSpaceNodeAncestors(spaceId, nodeId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes/${nodeId}/ancestors`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function searchSpaceNodes(spaceId, { query, withinNodeId = null, scope = 'folder', type = null, offset = 0, limit = 100 }) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const params = new URLSearchParams({ q: query, scope, offset: String(offset), limit: String(limit) });
  if (withinNodeId) params.set('within_node_id', withinNodeId);
  if (type) params.set('type', type);
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/search?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function updateSpace(spaceId, updates) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function deleteSpace(spaceId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function addSpaceItems(spaceId, items) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/items`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function createSpaceNode(spaceId, payload) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function createSpaceSkill(spaceId, payload) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/skills`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function moveSpaceNodes(spaceId, nodeIds, targetParentId = null) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes/move`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ nodeIds, targetParentId }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function duplicateSpaceNodes(spaceId, nodeIds) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes/duplicate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ nodeIds }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function previewSpaceNodeDeletion(spaceId, nodeIds) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes/delete-preview`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ nodeIds }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function deleteSpaceNodes(spaceId, nodeIds) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/nodes`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ nodeIds }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function preflightAttachmentReferences(references) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/agent/attachment-references/preflight`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ references }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function uploadSpaceItem(spaceId, file, parentId = null) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const form = new FormData();
  form.append('file', file);
  if (parentId) form.append('parent_id', parentId);
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function removeSpaceItem(spaceId, itemId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/items/${itemId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function updateSpaceItem(spaceId, itemId, updates) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/spaces/${spaceId}/items/${itemId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function createCharacter(data) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/characters/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function updateCharacter(characterId, updates) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/characters/${characterId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getPlatformVoices(search = '', { limit, offset, languages } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const qs = new URLSearchParams();
  if (search) qs.set('search', search);
  if (typeof limit === 'number') qs.set('limit', String(limit));
  if (typeof offset === 'number') qs.set('offset', String(offset));
  if (Array.isArray(languages) && languages.length > 0) qs.set('languages', languages.join(','));
  const params = qs.toString() ? `?${qs.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/api/voices/platform${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function deleteCharacter(characterId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/characters/${characterId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Generate a scene for a project
 * @param {string} projectId - Project ID
 * @param {string} description - Text description of the scene
 * @param {File[]} images - Optional array of reference images
 * @param {number} duration - Optional duration in milliseconds (default 3000)
 * @returns {Promise<Object>} Generated scene data
 */
export async function generateScene(projectId, description, images = [], duration = null, options = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const { referenceImageUrls = null } = options;

  const formData = new FormData();
  formData.append('description', description);
  // Only append duration if explicitly provided (not null/undefined)
  if (duration !== null && duration !== undefined) {
    formData.append('duration', duration.toString());
  }
  
  // Append image files
  images.forEach((image) => {
    formData.append('files', image);
  });

  // Append reference image URLs (for existing assets/keyframes)
  if (referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
    formData.append('reference_image_urls', JSON.stringify(referenceImageUrls));
  }

  console.debug('[generateScene] Generating scene', {
    projectId,
    descriptionLength: description?.length,
    imageCount: images.length,
    referenceImageUrlCount: referenceImageUrls?.length || 0,
    duration: duration !== null ? `${duration}ms` : 'auto',
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/generate`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/generate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[generateScene] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[generateScene] request succeeded');
  return response.json();
}

/**
 * Submit an edit request for a motion-graphic scene
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID
 * @param {string} promptText - User feedback describing the edit
 * @returns {Promise<Object>} { success, started }
 */
export async function editMotionGraphicScene(projectId, sceneId, promptText) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('prompt', promptText);

  console.debug('[editMotionGraphicScene] Editing motion graphic', {
    projectId,
    sceneId,
    promptLength: promptText?.length,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/motion-graphic/edit`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/motion-graphic/edit`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[editMotionGraphicScene] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[editMotionGraphicScene] request succeeded');
  return response.json();
}


/**
 * Create an empty scene for manual planning workflows
 * @param {string} projectId - Project ID
 * @param {Object} options - Optional overrides (name, description, sceneIndex, duration)
 * @returns {Promise<Object>} Created scene payload
 */
export async function createBlankScene(projectId, options = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const payload = {};
  if (options.name) payload.name = options.name;
  if (options.description) payload.description = options.description;
  if (typeof options.sceneIndex === 'number') payload.scene_index = options.sceneIndex;
  if (typeof options.duration === 'number') payload.duration = options.duration;

  console.debug('[createBlankScene] Creating empty scene', {
    projectId,
    hasName: !!payload.name,
    hasDescription: !!payload.description,
    sceneIndex: payload.scene_index,
    duration: payload.duration,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/blank`,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/blank`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[createBlankScene] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[createBlankScene] request succeeded');
  return response.json();
}

/**
 * Trigger keyframe generation for a scene
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID
 * @returns {Promise<Object>} { success, started }
 */
export async function startKeyframeGeneration(projectId, sceneId, options = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const { model = null } = options || {};
  const query = model ? `?model=${encodeURIComponent(model)}` : '';

  console.debug('[startKeyframeGeneration] Starting keyframe generation', {
    projectId,
    sceneId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/generate${query}`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/generate${query}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = await response.clone().text().catch(() => null);
    const error = await response.json().catch(() => ({}));
    console.error('[startKeyframeGeneration] request failed', {
      status: response.status,
      statusText: response.statusText,
      responseText: text,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[startKeyframeGeneration] request succeeded');
  return response.json();
}

/**
 * Update motion (prompt/duration) for a shot in a scene's plan
 * @param {string} projectId
 * @param {string} sceneId
 * @param {number} shotIndex
 * @param {string} prompt
 * @param {number} durationSec
 */
export async function updateSceneMotion(projectId, sceneId, shotIndex, prompt, durationSec) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/plan/motion`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ shotIndex, prompt, duration: durationSec }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Regenerate a specific keyframe image with prompt and optional reference images (single use)
 * @param {string} projectId
 * @param {string} sceneId
 * @param {string} keyframeId
 * @param {string} prompt
 * @param {File[]} images
 */
export async function regenerateKeyframe(projectId, sceneId, keyframeId, prompt, images = [], options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const { model = null } = options || {};

  const formData = new FormData();
  formData.append('prompt', prompt);
  images.forEach((image) => formData.append('files', image));
  if (model) {
    formData.append('model', model);
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}/regenerate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update a specific keyframe with prompt and optional reference images (iterative editing)
 * @param {string} projectId
 * @param {string} sceneId
 * @param {string} keyframeId
 * @param {string} prompt
 * @param {File[]} images
 * @param {Object} options - Optional parameters
 * @param {boolean} options.preview - If true, generates image without persisting to DB
 * @param {string} options.commitUrl - If provided, commits this URL without regenerating
 */
export async function updateKeyframe(projectId, sceneId = null, keyframeId, prompt, images = [], options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const { preview = false, commitUrl = null, commitUpload = false, referenceImageUrls = null, model = null } = options || {};

  const formData = new FormData();
  formData.append('prompt', prompt);
  images.forEach((image) => formData.append('files', image));
  if (model) {
    formData.append('model', model);
  }
  
  if (preview) {
    formData.append('preview', 'true');
  }
  if (commitUrl) {
    formData.append('commit_url', commitUrl);
  }
  if (commitUpload) {
    formData.append('commit_upload', 'true');
  }
  if (referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
    formData.append('reference_image_urls', JSON.stringify(referenceImageUrls));
  }

  // Use project-level route when sceneId is null (video-centric keyframes)
  const url = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}`
    : `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}`;

  console.debug('[updateKeyframe] Updating keyframe', {
    projectId,
    sceneId,
    keyframeId,
    promptLength: prompt?.length,
    imageCount: images.length,
    preview,
    commitUrl: commitUrl ? '(provided)' : null,
    referenceImageUrlCount: referenceImageUrls?.length || 0,
    usingProjectLevelRoute: !sceneId,
  });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[updateKeyframe] request succeeded');
  return response.json();
}

/**
 * Create a new keyframe for a scene with prompt and optional reference images
 * @param {string} projectId
 * @param {string} sceneId
 * @param {string} prompt
 * @param {File[]} images
 * @param {Object} options - Optional parameters
 * @param {string[]} options.referenceImageUrls - Array of existing asset image URLs
 */
export async function createKeyframe(projectId, sceneId, prompt, images = [], options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const { referenceImageUrls = null, model = null } = options || {};

  const formData = new FormData();
  formData.append('prompt', prompt);
  images.forEach((image) => formData.append('files', image));
  if (model) {
    formData.append('model', model);
  }
  
  if (referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
    formData.append('reference_image_urls', JSON.stringify(referenceImageUrls));
  }

  console.debug('[createKeyframe] Creating keyframe', {
    projectId,
    sceneId,
    promptLength: prompt?.length,
    imageCount: images.length,
    referenceImageUrlCount: referenceImageUrls?.length || 0,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[createKeyframe] request succeeded');
  return response.json();
}

/**
 * Create a project-level keyframe (no sceneId).
 * @param {string} projectId
 * @param {string} prompt
 * @param {File[]} images
 * @param {Object} options
 * @param {string[]} options.referenceImageUrls
 * @param {string|null} options.model
 */
export async function createProjectKeyframe(projectId, prompt, images = [], options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const { referenceImageUrls = null, model = null } = options || {};

  const formData = new FormData();
  formData.append('prompt', prompt || '');
  images.forEach((image) => formData.append('files', image));
  if (model) {
    formData.append('model', model);
  }
  if (referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
    formData.append('reference_image_urls', JSON.stringify(referenceImageUrls));
  }

  console.debug('[createProjectKeyframe] Creating project keyframe', {
    projectId,
    promptLength: (prompt || '').length,
    imageCount: images.length,
    referenceImageUrlCount: referenceImageUrls?.length || 0,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/keyframes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate 4 action frame keyframes using panel generation.
 * Takes a video prompt (required) and optionally existing keyframe IDs as references.
 * @param {string} projectId - Project ID
 * @param {string} prompt - Video prompt (required)
 * @param {string[]} referenceKeyframeIds - Optional array of keyframe IDs to use as references
 * @param {Object} options - Additional options
 * @param {string} options.aspectRatio - Aspect ratio for generated images (default from project)
 * @returns {Promise<Object>} { success, keyframes: [{ id, keyframeUrl, prompt, index }] }
 */
export async function generateActionFrames(projectId, prompt, referenceKeyframeIds = [], options = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  if (!projectId) throw new Error('Project ID is required');
  if (!prompt || !prompt.trim()) throw new Error('Prompt is required');

  const { aspectRatio = null } = options;

  const formData = new FormData();
  formData.append('prompt', prompt.trim());
  
  if (referenceKeyframeIds && referenceKeyframeIds.length > 0) {
    // Filter out pending/invalid IDs
    const validIds = referenceKeyframeIds.filter(id => id && !id.startsWith('pending-'));
    if (validIds.length > 0) {
      formData.append('reference_keyframe_ids', JSON.stringify(validIds));
    }
  }
  
  if (aspectRatio) {
    formData.append('aspect_ratio', aspectRatio);
  }

  console.debug('[generateActionFrames] Generating action frames', {
    projectId,
    promptLength: prompt.length,
    referenceCount: referenceKeyframeIds?.length || 0,
    aspectRatio,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/keyframes/generate-action-frames`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateActionFrames] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateActionFrames] request succeeded');
  return response.json();
}

/**
 * Duplicate an existing project-level keyframe (new ID) without copying the underlying blob.
 * @param {string} projectId
 * @param {string} keyframeId
 * @returns {Promise<Object>} { success, keyframeId, keyframeUrl, prompt }
 */
export async function duplicateProjectKeyframe(projectId, keyframeId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  if (!projectId) throw new Error('Project ID is required');
  if (!keyframeId) throw new Error('Keyframe ID is required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/duplicate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update a project-level video (video-centric timeline).
 * @param {string} projectId
 * @param {string} videoId
 * @param {Object} updates
 */
export async function updateProjectVideo(projectId, videoId, updates) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/** Persist non-destructive color adjustments without creating a new media version. */
export async function updateVideoColorGrade(projectId, videoId, colorGrade) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/color-grade`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ colorGrade }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

export async function restoreProjectVideoVersion(projectId, videoId, versionNumber) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/restore-version`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ versionNumber }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Adjust playback speed of a project-level video (re-encodes and replaces videoUrl).
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to adjust
 * @param {number} playbackSpeed - Target playback speed
 * @returns {Promise<Object>} Updated video data
 */
export async function adjustProjectVideoPlaybackSpeed(projectId, videoId, playbackSpeed) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/playback-speed`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playbackSpeed }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

export async function restoreProjectKeyframeVersion(projectId, keyframeId, versionNumber) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/restore-version`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ versionNumber }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete a scene from a project
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID (UUID)
 * @returns {Promise<Object>} Success response
 */
export async function deleteScene(projectId, sceneId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[deleteScene] Deleting scene', {
    projectId,
    sceneId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[deleteScene] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[deleteScene] request succeeded');
  return response.json();
}

/**
 * Update scene crop values (soft crop, only applied on export)
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID (UUID)
 * @param {number} cropStartMs - Milliseconds trimmed from start
 * @param {number} cropEndMs - Milliseconds trimmed from end
 * @returns {Promise<Object>} Updated scene crop values
 */
export async function updateSceneCrop(projectId, sceneId, cropStartMs, cropEndMs) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateSceneCrop] Updating scene crop', {
    projectId,
    sceneId,
    cropStartMs,
    cropEndMs,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/crop`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/crop`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ cropStartMs, cropEndMs }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[updateSceneCrop] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[updateSceneCrop] request succeeded');
  return response.json();
}

/**
 * Update scene volume level
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID (UUID)
 * @param {number} volume - Volume level (0-100)
 * @returns {Promise<Object>} Updated scene volume
 */
export async function updateSceneVolume(projectId, sceneId, volume) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateSceneVolume] Updating scene volume', {
    projectId,
    sceneId,
    volume,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/volume`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/volume`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ volume }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[updateSceneVolume] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[updateSceneVolume] request succeeded');
  return response.json();
}

/**
 * Reorder scenes within a project
 * @param {string} projectId - Project ID
 * @param {string[]} sceneIds - Ordered array of scene IDs
 * @returns {Promise<Object>} Updated scenes with new order indices
 */
export async function reorderScenes(projectId, sceneIds) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[reorderScenes] Reordering scenes', {
    projectId,
    sceneCount: sceneIds.length,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/reorder`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/reorder`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ scene_ids: sceneIds }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[reorderScenes] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[reorderScenes] request succeeded');
  return response.json();
}

/**
 * Approve a scene and start video generation
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID to approve
 * @returns {Promise<Object>} Video generation response
 */
export async function approveSceneVideo(projectId, sceneId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[approveSceneVideo] Starting video generation for scene', {
    projectId,
    sceneId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/video/generate`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/video/generate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[approveSceneVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[approveSceneVideo] request succeeded');
  return response.json();
}

/**
 * Generate (or regenerate) a single video entity (motion clip).
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to generate
 * @returns {Promise<Object>} Video generation response
 */
export async function generateSingleVideo(projectId, videoId, options = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[generateSingleVideo] Starting video generation for video', {
    projectId,
    videoId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/generate`,
  });

  const body = {};
  if (options.aspectRatio) body.aspect_ratio = options.aspectRatio;

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/generate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateSingleVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateSingleVideo] request succeeded');
  return response.json();
}

/**
 * Upscale a video using the Crystal Upscaler.
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to upscale
 * @param {object} options - Optional upscale settings
 * @param {number} [options.scaleFactor=2] - Scale factor (1-4)
 * @returns {Promise<Object>} Video upscaling response
 */
export async function upscaleVideo(projectId, videoId, options = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const payload = {};
  if (options.scaleFactor != null) payload.scaleFactor = options.scaleFactor;
  if (options.uploadToCdn != null) payload.uploadToCdn = options.uploadToCdn;

  console.debug('[upscaleVideo] Starting video upscaling', {
    projectId,
    videoId,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/upscale`,
    payload,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/upscale`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[upscaleVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[upscaleVideo] request succeeded');
  return response.json();
}

/**
 * Add or remove captions from a video via ZapCap.
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID
 * @param {boolean} enabled - true = add captions, false = remove (revert to base)
 * @param {object} [style] - ZapCap style parameters (only used when enabled=true)
 * @returns {Promise<Object>}
 */
export async function toggleVideoCaptions(projectId, videoId, enabled, style = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/captions`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ enabled, style }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Batch upscale multiple videos using the Crystal Upscaler.
 * @param {string} projectId - Project ID
 * @param {string[]} videoIds - Array of video IDs to upscale
 * @param {object} options - Optional upscale settings
 * @param {number} [options.scaleFactor=2] - Scale factor (1-4)
 * @returns {Promise<Object>} Batch upscaling response
 */
export async function batchUpscaleVideos(projectId, videoIds, options = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const payload = {
    videoIds,
  };
  if (options.scaleFactor != null) payload.scaleFactor = options.scaleFactor;

  console.debug('[batchUpscaleVideos] Starting batch video upscaling', {
    projectId,
    videoIds,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/videos/batch-upscale`,
    payload,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/batch-upscale`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[batchUpscaleVideos] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[batchUpscaleVideos] request succeeded');
  return response.json();
}

/**
 * Update scene voiceover text and regenerate voiceover audio
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID to update voiceover for
 * @param {string} voiceoverText - New voiceover text
 * @returns {Promise<Object>} Voiceover regeneration response
 */
export async function updateSceneVoiceover(projectId, sceneId, voiceoverText) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateSceneVoiceover] Updating scene voiceover', {
    projectId,
    sceneId,
    voiceoverLength: voiceoverText?.length,
    backendUrl: `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/voiceover`
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/voiceover`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ voiceover: voiceoverText }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[updateSceneVoiceover] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error
    });
    throw createApiError(response, error);
  }

  console.debug('[updateSceneVoiceover] request succeeded');
  return response.json();
}

// ============================================================================
// Voice API Functions
// ============================================================================

/**
 * Get all voices for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} List of voices
 */
export async function getProjectVoices(projectId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/voices`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate voice preview using ElevenLabs
 * Does NOT create a database record - just generates preview for user to listen to
 * @param {string} projectId - Project ID
 * @param {string} description - Voice description/prompt
 * @returns {Promise<Object>} { success, previewUrl, generatedVoiceId }
 */
export async function generateVoicePreview(projectId, description) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[generateVoicePreview] Generating preview', { projectId, descriptionLength: description?.length });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/voices/generate-preview`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ description }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Save voice after preview - creates database record and saves to ElevenLabs
 * @param {string} projectId - Project ID
 * @param {string} description - Voice description
 * @param {string} name - Voice name
 * @param {string} generatedVoiceId - Temporary voice ID from preview generation
 * @param {string} [previewAudioBase64] - Optional base64 encoded preview audio to upload to CDN
 * @returns {Promise<Object>} Saved voice
 */
export async function saveVoice(projectId, description, name, generatedVoiceId, previewAudioBase64 = null) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[saveVoice] Saving voice', { projectId, name, generatedVoiceId, hasPreviewAudio: !!previewAudioBase64 });

  const body = {
    description,
    name,
    generatedVoiceId,
  };

  // Include preview audio if provided
  if (previewAudioBase64) {
    body.previewAudioBase64 = previewAudioBase64;
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/voices`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Clone a voice from an uploaded audio sample (ElevenLabs Instant Voice Cloning).
 * @param {string} projectId - Project ID
 * @param {File} audioFile - Audio sample to clone from
 * @param {string} name - Voice name
 * @param {string} [description] - Optional description stored on the voice record
 * @returns {Promise<Object>} { success, voice }
 */
export async function cloneVoiceFromAudio(projectId, audioFile, name, description = 'Cloned from uploaded audio') {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  if (!audioFile) {
    throw new Error('No audio file provided');
  }

  if (!name || !String(name).trim()) {
    throw new Error('Voice name is required');
  }

  const formData = new FormData();
  formData.append('file', audioFile);
  formData.append('name', String(name).trim());
  if (description) {
    formData.append('description', String(description));
  }

  console.debug('[cloneVoiceFromAudio] Cloning voice', {
    projectId,
    name,
    fileName: audioFile.name,
    fileSize: audioFile.size,
    fileType: audioFile.type,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/voices/clone`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get every voice the current user has generated (user-level + across all their projects).
 * @param {Object} [options]
 * @param {string} [options.search] - Filter by name/description
 * @param {number} [options.limit]
 * @param {number} [options.offset]
 * @param {boolean} [options.attachableOnly] - Require a usable preview URL
 * @returns {Promise<Object>} { success, voices, total, limit, offset, hasMore }
 */
export async function getMyVoices({
  search,
  limit,
  offset,
  attachableOnly = false,
} = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const params = new URLSearchParams();
  if (search && search.trim()) params.set('search', search.trim());
  if (Number.isFinite(limit)) params.set('limit', String(limit));
  if (Number.isFinite(offset)) params.set('offset', String(offset));
  if (attachableOnly) params.set('attachable_only', 'true');
  const qs = params.toString();

  const response = await fetch(`${API_BASE_URL}/api/voices${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate text-to-speech with a saved voice (Seed Audio, using the voice preview as reference).
 * @param {string} voiceId - Voice ID
 * @param {string} text - Text to synthesize
 * @param {Object} [options]
 * @param {number} [options.speed] - 0.5-2.0 speed multiplier
 * @param {number} [options.pitch] - -12..12 semitones
 * @returns {Promise<Object>} { success, audioUrl, duration, voiceId, text }
 */
export async function generateVoiceTts(voiceId, text, { speed, pitch } = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const body = { text };
  if (Number.isFinite(speed)) body.speed = speed;
  if (Number.isFinite(pitch)) body.pitch = pitch;

  console.debug('[generateVoiceTts] Generating TTS', { voiceId, textLength: text?.length, speed, pitch });

  const response = await fetch(`${API_BASE_URL}/api/voices/${voiceId}/tts`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate a brand new voice from scratch with Seed Audio.
 * The voice can be steered by the prompt alone, up to 3 reference audio clips
 * (tag them @Audio1..@Audio3 in the prompt), or a single reference image.
 * Audio and image references are mutually exclusive.
 * @param {Object} params
 * @param {string} params.prompt - Full generation prompt (voice description + text to speak)
 * @param {File[]} [params.audioFiles] - Up to 3 reference audio files
 * @param {File} [params.imageFile] - Single reference image
 * @param {string[]} [params.audioUrls] - Existing reference audio URLs
 * @param {string} [params.imageUrl] - Existing reference image URL
 * @returns {Promise<Object>} { success, previewUrl, duration, referenceAudioUrls, referenceImageUrl }
 */
export async function generateSeedVoicePreview({ prompt, audioFiles = [], imageFile = null, audioUrls = [], imageUrl = null }) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('prompt', prompt);
  (audioFiles || []).forEach((file) => formData.append('audio_files', file));
  if (imageFile) formData.append('image_file', imageFile);
  if (audioUrls && audioUrls.length > 0) formData.append('audio_urls', JSON.stringify(audioUrls));
  if (imageUrl) formData.append('image_url', imageUrl);

  console.debug('[generateSeedVoicePreview] Generating voice preview', {
    promptLength: prompt?.length,
    audioFileCount: (audioFiles || []).length,
    hasImageFile: !!imageFile,
    audioUrlCount: (audioUrls || []).length,
    hasImageUrl: !!imageUrl,
  });

  const response = await fetch(`${API_BASE_URL}/api/voices/seed/preview`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Save a Seed Audio generated preview as a new user-level voice.
 * @param {Object} params
 * @param {string} params.name - Voice name
 * @param {string} params.description - Prompt/description used to generate the voice
 * @param {string} params.previewUrl - CDN URL returned by generateSeedVoicePreview
 * @returns {Promise<Object>} { success, voice }
 */
export async function saveSeedVoice({ name, description, previewUrl }) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/voices/seed/save`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, description, previewUrl }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Rename any voice the user owns (user-level or in one of their projects).
 * @param {string} voiceId - Voice ID
 * @param {string} name - New name
 * @returns {Promise<Object>} { success, voice }
 */
export async function renameMyVoice(voiceId, name) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/voices/${voiceId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete any voice the user owns (user-level or in one of their projects).
 * @param {string} voiceId - Voice ID
 * @returns {Promise<Object>} { success, message }
 */
export async function deleteMyVoice(voiceId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/voices/${voiceId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update voice name
 * @param {string} projectId - Project ID
 * @param {string} voiceId - Voice ID to update
 * @param {string} name - New name
 * @returns {Promise<Object>} Updated voice
 */
export async function updateVoiceName(projectId, voiceId, name) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/voices/${voiceId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete a voice
 * @param {string} projectId - Project ID
 * @param {string} voiceId - Voice ID to delete
 * @returns {Promise<Object>} Success response
 */
export async function deleteVoice(projectId, voiceId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/voices/${voiceId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate a voiceover audio clip using a custom voice
 * VIDEO-CENTRIC: Now accepts either sceneId OR videoId (videoId takes priority)
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID to attach the audio clip to (deprecated, use videoId)
 * @param {string} script - The text to be spoken
 * @param {string} voiceId - ID of the custom voice to use
 * @param {number} startTimestamp - Start time in milliseconds
 * @param {string|null} videoId - Video ID to attach the audio clip to (video-centric)
 * @param {number} speed - Speech speed multiplier (0.5-2.0, default 1.0)
 * @returns {Promise<Object>} Created audio clip
 */
export async function generateVoiceover(projectId, sceneId, script, voiceId, startTimestamp, videoId = null, speed = 1.0) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Determine whether to use video-centric or scene-centric endpoint
  const useVideoCentric = !!videoId;
  const targetId = videoId || sceneId;
  const targetType = useVideoCentric ? 'videos' : 'scenes';

  console.debug('[generateVoiceover] Generating voiceover', { 
    projectId, 
    sceneId, 
    videoId,
    useVideoCentric,
    scriptLength: script?.length,
    voiceId,
    startTimestamp,
    speed
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/${targetType}/${targetId}/audio-clips/generate-voiceover`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      script, 
      voiceId,
      startTimestamp: Math.round(startTimestamp),
      speed
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate a sound effect audio clip using ElevenLabs
 * VIDEO-CENTRIC: Now accepts either sceneId OR videoId (videoId takes priority)
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID to add the audio clip to (deprecated, use videoId)
 * @param {string} prompt - Text description of the sound effect
 * @param {number} startTimestamp - Start time in milliseconds
 * @param {number|null} durationSeconds - Optional duration in seconds (0.5-22)
 * @param {number} promptInfluence - How closely to follow the prompt (0-1, default 0.3)
 * @param {string|null} videoId - Video ID to attach the audio clip to (video-centric)
 * @param {boolean} isLoop - When true, allows extending duration beyond source by looping
 * @returns {Promise<Object>} Created audio clip
 */
export async function generateSoundEffect(projectId, sceneId, prompt, startTimestamp, durationSeconds = null, promptInfluence = 0.3, videoId = null, isLoop = false, audioClipId = null) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Determine whether to use video-centric or scene-centric endpoint
  const useVideoCentric = !!videoId;
  const targetId = videoId || sceneId;
  const targetType = useVideoCentric ? 'videos' : 'scenes';

  console.debug('[generateSoundEffect] Generating sound effect', { 
    projectId, 
    sceneId, 
    videoId,
    audioClipId,
    useVideoCentric,
    promptLength: prompt?.length,
    startTimestamp,
    durationSeconds,
    promptInfluence,
    isLoop
  });

  const body = { 
    prompt, 
    startTimestamp: Math.round(startTimestamp),
    promptInfluence,
    isLoop,
  };
  
  // Only add duration if specified (null means auto)
  if (durationSeconds !== null) {
    body.durationSeconds = durationSeconds;
  }
  
  // Include audioClipId for regeneration (update existing clip)
  if (audioClipId) {
    body.audioClipId = audioClipId;
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/${targetType}/${targetId}/audio-clips/generate-sound-effect`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update an audio clip's position (scene/video and timestamp)
 * @param {string} projectId - Project ID
 * @param {string} audioClipId - Audio clip ID to update
 * @param {string} newSceneId - New scene ID to move the clip to (for scene mode)
 * @param {number} startTimestamp - New start timestamp (relative to the new scene/video)
 * @param {string} [newVideoId] - New video ID to move the clip to (for video-centric mode)
 * @returns {Promise<Object>} Updated audio clip
 */
export async function updateAudioClipPosition(projectId, audioClipId, newSceneId, startTimestamp, newVideoId = null) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateAudioClipPosition] Updating position', { 
    projectId, 
    audioClipId, 
    newSceneId,
    newVideoId,
    startTimestamp 
  });

  const body = { 
    startTimestamp: Math.round(startTimestamp)
  };
  
  // Use videoId if provided (video-centric mode), otherwise use sceneId
  if (newVideoId) {
    body.newVideoId = newVideoId;
  }
  if (newSceneId) {
    body.newSceneId = newSceneId;
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/audio-clips/${audioClipId}/position`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update an audio clip's timing (startTimestamp + duration)
 * @param {string} projectId - Project ID
 * @param {string} audioClipId - Audio clip ID to update
 * @param {number} startTimestamp - New start timestamp (relative to the scene)
 * @param {number} duration - New duration in milliseconds
 * @returns {Promise<Object>} Updated audio clip
 */
export async function updateAudioClipTiming(projectId, audioClipId, startTimestamp, duration, trimStartMs = null) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateAudioClipTiming] Updating timing', { 
    projectId, 
    audioClipId, 
    startTimestamp,
    duration,
    trimStartMs,
  });

  const body = {
    startTimestamp: Math.round(startTimestamp),
    duration: Math.round(duration),
  };
  if (trimStartMs !== null && trimStartMs !== undefined) {
    body.trimStartMs = Math.round(trimStartMs);
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/audio-clips/${audioClipId}/timing`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update an audio clip's volume (0-200%)
 * @param {string} projectId - Project ID
 * @param {string} audioClipId - Audio clip ID to update
 * @param {number} volume - Volume percentage (0-200)
 * @returns {Promise<Object>} Updated audio clip
 */
export async function updateAudioClipVolume(projectId, audioClipId, volume) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const volInt = Math.round(Number(volume) || 0);

  console.debug('[updateAudioClipVolume] Updating volume', {
    projectId,
    audioClipId,
    volume: volInt,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/audio-clips/${audioClipId}/volume`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ volume: volInt }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Generate a music audio clip using ElevenLabs
 * VIDEO-CENTRIC: Now accepts either sceneId OR videoId (videoId takes priority)
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID to add the audio clip to (deprecated, use videoId)
 * @param {string} prompt - Text description of the music
 * @param {number} startTimestamp - Start time in milliseconds
 * @param {number} durationSeconds - Duration in seconds (15-120)
 * @param {string|null} videoId - Video ID to attach the audio clip to (video-centric)
 * @param {string|null} audioClipId - Existing audio clip ID to update (for regeneration)
 * @returns {Promise<Object>} Created or updated audio clip
 */
export async function generateMusic(projectId, sceneId, prompt, startTimestamp, durationSeconds = 30, videoId = null, audioClipId = null) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Determine whether to use video-centric or scene-centric endpoint
  const useVideoCentric = !!videoId;
  const targetId = videoId || sceneId;
  const targetType = useVideoCentric ? 'videos' : 'scenes';

  const resolvedDurationSeconds = Number.isFinite(Number(durationSeconds))
    ? Number(durationSeconds)
    : 30;
  
  console.debug('[generateMusic] Generating music', { 
    projectId, 
    sceneId, 
    videoId,
    audioClipId,
    useVideoCentric,
    promptLength: prompt?.length,
    startTimestamp,
    durationSeconds: resolvedDurationSeconds
  });

  const body = { 
    prompt, 
    startTimestamp: Math.round(startTimestamp),
    durationSeconds: resolvedDurationSeconds,
    duration_seconds: resolvedDurationSeconds,
  };
  
  // Include audioClipId for regeneration (update existing clip)
  if (audioClipId) {
    body.audioClipId = audioClipId;
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/${targetType}/${targetId}/audio-clips/generate-music`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Lip-sync a voiceover audio clip to selected videos.
 * @param {string} projectId
 * @param {string} audioClipId
 * @param {string[]} videoIds
 * @returns {Promise<Object>} Updated video URLs + skipped list
 */
export async function lipsyncVoiceover(projectId, audioClipId, videoIds = []) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/audio-clips/${audioClipId}/lipsync`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ videoIds }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Upload a user-provided audio clip and attach it to a video
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to attach the audio clip to
 * @param {File} audioFile - Audio file to upload
 * @param {number} startTimestamp - Start time in milliseconds
 * @returns {Promise<Object>} Created audio clip
 */
export async function uploadAudio(projectId, videoId, audioFile, startTimestamp) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  if (!audioFile) {
    throw new Error('No audio file provided');
  }

  const formData = new FormData();
  formData.append('file', audioFile);
  formData.append('start_timestamp', Math.max(0, Math.round(startTimestamp || 0)));

  console.debug('[uploadAudio] Uploading audio clip', {
    projectId,
    videoId,
    fileName: audioFile.name,
    fileSize: audioFile.size,
    fileType: audioFile.type,
    startTimestamp,
  });

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/audio-clips/upload`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete an audio clip
 * @param {string} projectId - Project ID
 * @param {string} audioClipId - Audio clip ID to delete
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteAudioClip(projectId, audioClipId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[deleteAudioClip] Deleting audio clip', { 
    projectId, 
    audioClipId
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/audio-clips/${audioClipId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Duplicate an audio clip within the same project.
 * The duplicated clip is placed right after the original on the timeline.
 * @param {string} projectId - Project ID
 * @param {string} audioClipId - Audio clip ID to duplicate
 * @returns {Promise<Object>} The newly created audio clip
 */
export async function duplicateAudioClip(projectId, audioClipId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[duplicateAudioClip] Duplicating audio clip', { 
    projectId, 
    audioClipId
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/audio-clips/${audioClipId}/duplicate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Extract audio from a video and create an audio clip attached to that video.
 * The audio clip will start at timestamp 0.
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to extract audio from
 * @returns {Promise<Object>} Created audio clip data
 */
export async function extractAudioFromVideo(projectId, videoId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[extractAudioFromVideo] Extracting audio from video', { 
    projectId, 
    videoId
  });

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/audio-clips/extract-from-video`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

// ============================================================================
// Overlay Clips (B-roll/overlay video track)
// ============================================================================

/**
 * Create a new overlay clip backed by an existing source Video.
 * @param {string} projectId - Project ID
 * @param {Object} data - { sourceVideoId (required), videoId, startMs, durationMs, sourceDurationMs, opacity, zIndex }
 * @returns {Promise<Object>} Created overlay clip
 */
export async function createOverlayClip(projectId, data) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[createOverlayClip] Creating overlay clip', { projectId, ...data });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/overlay-clips`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Create an overlay clip from an existing project video (source Video id is the
 * URL path param).
 * @param {string} projectId - Project ID
 * @param {string} videoId - Source video ID (goes in the URL path)
 * @param {Object} data - { videoId (parent anchor), startMs, durationMs, opacity, zIndex }
 * @returns {Promise<Object>} Created overlay clip
 */
export async function createOverlayClipFromVideo(projectId, videoId, data) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[createOverlayClipFromVideo] Creating overlay from video', { projectId, videoId, ...data });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/overlay-clips/from-video/${videoId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update overlay clip properties.
 * @param {string} projectId - Project ID
 * @param {string} clipId - Overlay clip ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated overlay clip
 */
export async function updateOverlayClip(projectId, clipId, updates) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[updateOverlayClip] Updating overlay clip', { projectId, clipId, ...updates });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/overlay-clips/${clipId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update overlay clip position on the timeline.
 * @param {string} projectId - Project ID
 * @param {string} clipId - Overlay clip ID
 * @param {number} startMs - New start position (ms)
 * @returns {Promise<Object>} Updated overlay clip
 */
export async function updateOverlayClipPosition(projectId, clipId, startMs, videoId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[updateOverlayClipPosition] Moving overlay clip', { projectId, clipId, startMs, videoId });

  const body = { startMs: Math.round(startMs) };
  if (videoId) body.videoId = videoId;

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/overlay-clips/${clipId}/position`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update overlay clip timing (trim + duration).
 * @param {string} projectId - Project ID
 * @param {string} clipId - Overlay clip ID
 * @param {number} trimStartMs - Trim offset into source video (ms)
 * @param {number} durationMs - Visible duration (ms)
 * @returns {Promise<Object>} Updated overlay clip
 */
export async function updateOverlayClipTiming(projectId, clipId, trimStartMs, durationMs) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[updateOverlayClipTiming] Updating overlay clip timing', { projectId, clipId, trimStartMs, durationMs });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/overlay-clips/${clipId}/timing`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ trimStartMs: Math.round(trimStartMs), durationMs: Math.round(durationMs) }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete an overlay clip.
 * @param {string} projectId - Project ID
 * @param {string} clipId - Overlay clip ID
 * @returns {Promise<Object>}
 */
export async function deleteOverlayClip(projectId, clipId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  console.debug('[deleteOverlayClip] Deleting overlay clip', { projectId, clipId });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/overlay-clips/${clipId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Update a video/motion's properties (prompt, duration, playbackSpeed)
 * This does NOT re-encode the video - use adjustVideoPlaybackSpeed for that
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID
 * @param {string} videoId - Video ID to update
 * @param {Object} updates - Fields to update (prompt, duration, playbackSpeed)
 * @returns {Promise<Object>} Updated video data
 */
export async function updateVideo(projectId, sceneId, videoId, updates) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateVideo] Updating video', { 
    projectId, 
    sceneId,
    videoId,
    updates
  });

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/videos/${videoId}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete a video/motion from a scene.
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID (optional for project-level videos)
 * @param {string} videoId - Video ID to delete
 * @returns {Promise<Object>} Delete response
 */
export async function deleteVideo(projectId, sceneId, videoId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[deleteVideo] Deleting video', {
    projectId,
    sceneId,
    videoId,
  });

  const endpoint = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/videos/${videoId}`
    : `${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}`;

  const response = await fetch(endpoint, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Duplicate a project-level video.
 * Creates a new video adjacent to the source, with duplicated keyframe records.
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to duplicate
 * @returns {Promise<Object>} Duplicated video payload
 */
export async function duplicateVideo(projectId, videoId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[duplicateVideo] Duplicating video', {
    projectId,
    videoId,
  });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/duplicate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Adjust playback speed of a video
 * @param {string} projectId - Project ID
 * @param {string} sceneId - Scene ID
 * @param {string} videoId - Video ID to adjust
 * @param {number} playbackSpeed - Target playback speed (e.g., 2.0 for 2x faster, 0.5 for half speed)
 * @returns {Promise<Object>} Updated video data
 */
export async function adjustVideoPlaybackSpeed(projectId, sceneId, videoId, playbackSpeed) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[adjustVideoPlaybackSpeed] Adjusting playback speed', { 
    projectId, 
    sceneId,
    videoId,
    playbackSpeed
  });

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/videos/${videoId}/playback-speed`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playbackSpeed }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

// ============================================================================
// Magic Select API Functions (SAM-3 Segmentation)
// ============================================================================

/**
 * Perform magic select segmentation on a keyframe using a bounding box from lasso selection.
 * Uses SAM-3 image-to-image with box_prompts and apply_mask=True for direct masked object extraction.
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID (optional - for video-centric model, pass null)
 * @param {string} keyframeId - Keyframe ID
 * @param {Object} box - Bounding box from lasso selection { x1, y1, x2, y2 } (normalized 0-1 coordinates)
 * @param {string|null} imageUrl - Optional image URL override for client-side edited images
 * @returns {Promise<Object>} { success, maskedImageUrl, maskOverlayUrl, bounds, imageWidth, imageHeight }
 */
export async function magicSelectKeyframe(projectId, sceneId, keyframeId, box, imageUrl = null) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  // Send box coordinates (normalized 0-1)
  formData.append('box_x1', box.x1.toString());
  formData.append('box_y1', box.y1.toString());
  formData.append('box_x2', box.x2.toString());
  formData.append('box_y2', box.y2.toString());
  
  // Handle blob URLs by converting them to file uploads
  // Blob URLs (blob:http://...) are browser-only and cannot be fetched by the backend
  let hasImageFile = false;
  if (imageUrl && imageUrl.startsWith('blob:')) {
    try {
      console.debug('[magicSelectKeyframe] Converting blob URL to file upload');
      const blobResponse = await fetch(imageUrl);
      const blob = await blobResponse.blob();
      const file = new File([blob], 'keyframe-image.png', { type: blob.type || 'image/png' });
      formData.append('image', file);
      hasImageFile = true;
    } catch (err) {
      console.warn('[magicSelectKeyframe] Failed to convert blob URL to file:', err);
      throw new Error('Unable to process the current image. Please wait for the image to finish saving and try again.');
    }
  } else if (imageUrl) {
    formData.append('image_url', imageUrl);
  }

  console.debug('[magicSelectKeyframe] Segmenting keyframe with box', {
    projectId,
    sceneId,
    keyframeId,
    box,
    hasImageUrlOverride: !!imageUrl,
    hasImageFile,
  });

  // Use project-level route if sceneId is not provided (video-centric model)
  const url = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}/magic-select`
    : `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/magic-select`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[magicSelectKeyframe] Segmentation succeeded');
  return response.json();
}

/**
 * Extract (crop) the masked region from a keyframe (for duplicate action).
 * Takes the already-masked image URL and crops it to bounds.
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID (optional - for video-centric model, pass null)
 * @param {string} keyframeId - Keyframe ID
 * @param {string} maskedImageUrl - URL of masked object from magic-select
 * @param {Object} bounds - Bounding box { x, y, width, height } in pixels
 * @returns {Promise<Object>} { success, extractedImageUrl }
 */
export async function extractMagicSelectRegion(projectId, sceneId, keyframeId, maskedImageUrl, bounds) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('masked_image_url', maskedImageUrl);
  formData.append('bounds_x', bounds.x.toString());
  formData.append('bounds_y', bounds.y.toString());
  formData.append('bounds_width', bounds.width.toString());
  formData.append('bounds_height', bounds.height.toString());

  console.debug('[extractMagicSelectRegion] Extracting region', {
    projectId,
    sceneId,
    keyframeId,
    bounds,
  });

  // Use project-level route if sceneId is not provided (video-centric model)
  const url = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}/magic-select/extract`
    : `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/magic-select/extract`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[extractMagicSelectRegion] Extraction succeeded');
  return response.json();
}

/**
 * Delete the masked region from a keyframe (creates cutout with transparency).
 * Uses the masked image to identify which region to remove from the original.
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID (optional - for video-centric model, pass null)
 * @param {string} keyframeId - Keyframe ID
 * @param {string} maskedImageUrl - URL of masked object from magic-select
 * @returns {Promise<Object>} { success, cutoutImageUrl }
 */
export async function deleteMagicSelectRegion(projectId, sceneId, keyframeId, maskedImageUrl) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('masked_image_url', maskedImageUrl);

  console.debug('[deleteMagicSelectRegion] Creating cutout', {
    projectId,
    sceneId,
    keyframeId,
  });

  // Use project-level route if sceneId is not provided (video-centric model)
  const url = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}/magic-select/delete`
    : `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/magic-select/delete`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[deleteMagicSelectRegion] Cutout created');
  return response.json();
}

/**
 * Edit a layer or masked region on a keyframe.
 * For mask edits: Draws a red rectangle on the image at mask bounds in the background.
 * For layer edits: Sends the layer image directly, optionally removes background from result.
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID (optional - for video-centric model, pass null)
 * @param {string} keyframeId - Keyframe ID
 * @param {string} imageUrl - The image to edit (base keyframe URL or image layer URL)
 * @param {string} prompt - User's edit prompt
 * @param {string} editType - "mask" or "layer"
 * @param {Object} options - Optional parameters
 * @param {Object} options.maskBounds - For mask edits: { x, y, width, height } in pixels
 * @param {boolean} options.removeBackground - If true, remove background from result (for layer edits)
 * @returns {Promise<Object>} { success, editedImageUrl }
 */
export async function editLayer(projectId, sceneId, keyframeId, imageUrl, prompt, editType, options = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const { maskBounds = null, removeBackground = false, cropBounds = null, targetSize = null, model = null } = options;

  const formData = new FormData();
  
  // Handle blob URLs by converting them to file uploads
  let hasImageFile = false;
  if (imageUrl && imageUrl.startsWith('blob:')) {
    try {
      console.debug('[editLayer] Converting blob URL to file upload');
      const blobResponse = await fetch(imageUrl);
      const blob = await blobResponse.blob();
      const file = new File([blob], 'keyframe-image.png', { type: blob.type || 'image/png' });
      formData.append('image', file);
      hasImageFile = true;
    } catch (err) {
      console.warn('[editLayer] Failed to convert blob URL to file:', err);
      throw new Error('Unable to process the current image. Please wait for the image to finish saving and try again.');
    }
  } else if (imageUrl) {
    formData.append('image_url', imageUrl);
  }
  
  formData.append('prompt', prompt);
  formData.append('edit_type', editType);
  formData.append('remove_background', removeBackground.toString());
  if (model) {
    formData.append('model', model);
  }

  // Add mask bounds if provided (for mask edits)
  if (maskBounds) {
    formData.append('mask_bounds_x', maskBounds.x.toString());
    formData.append('mask_bounds_y', maskBounds.y.toString());
    formData.append('mask_bounds_width', maskBounds.width.toString());
    formData.append('mask_bounds_height', maskBounds.height.toString());
  }
  
  // Add crop bounds if provided (for layer edits to avoid sending full-canvas images)
  if (cropBounds) {
    formData.append('crop_x', cropBounds.x.toString());
    formData.append('crop_y', cropBounds.y.toString());
    formData.append('crop_width', cropBounds.width.toString());
    formData.append('crop_height', cropBounds.height.toString());
  }
  
  // Add target size if provided (for layer edits; controls the "covering box" size)
  if (targetSize) {
    formData.append('target_width', targetSize.width.toString());
    formData.append('target_height', targetSize.height.toString());
  }

  console.debug('[editLayer] Editing layer', {
    projectId,
    sceneId,
    keyframeId,
    editType,
    promptLength: prompt?.length,
    hasMaskBounds: !!maskBounds,
    hasCropBounds: !!cropBounds,
    hasTargetSize: !!targetSize,
    hasImageFile,
    removeBackground,
    model,
  });

  // Use project-level route if sceneId is not provided (video-centric model)
  const url = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}/layer-edit`
    : `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/layer-edit`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[editLayer] Edit succeeded');
  return response.json();
}

/**
 * Change the camera angle of a keyframe image.
 * Uses the Qwen Image Edit 2511 Multiple Angles model to generate the same scene
 * from a different camera angle (azimuth/elevation).
 * @param {string} projectId - Project ID
 * @param {string|null} sceneId - Scene ID (optional - for video-centric model, pass null)
 * @param {string} keyframeId - Keyframe ID
 * @param {Object} params - Camera angle parameters
 * @param {number} params.horizontalAngle - 0°=front, 90°=right, 180°=back, 270°=left, 360°=front
 * @param {number} params.verticalAngle - -30°=low-angle, 0°=eye-level, up to 90°=bird's-eye
 * @param {number} params.zoom - 0=wide shot, 5=medium shot, 10=close-up
 * @param {boolean} params.preview - If true, returns preview without persisting
 * @param {string} params.commitUrl - If provided, commits this URL directly (skip generation)
 * @returns {Promise<Object>} { success, keyframeId, keyframeUrl, horizontalAngle, verticalAngle, zoom }
 */
export async function changeKeyframeCameraAngle(projectId, sceneId, keyframeId, params = {}) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    horizontalAngle = 0,
    verticalAngle = 0,
    zoom = 5,
    preview = true,
    commitUrl = null,
  } = params;

  const formData = new FormData();
  formData.append('horizontal_angle', horizontalAngle.toString());
  formData.append('vertical_angle', verticalAngle.toString());
  formData.append('zoom', zoom.toString());
  formData.append('preview', preview.toString());
  if (commitUrl) {
    formData.append('commit_url', commitUrl);
  }

  console.debug('[changeKeyframeCameraAngle] Changing camera angle', {
    projectId,
    sceneId,
    keyframeId,
    horizontalAngle,
    verticalAngle,
    zoom,
    preview,
    hasCommitUrl: !!commitUrl,
  });

  // Use project-level route if sceneId is not provided (video-centric model)
  const url = sceneId
    ? `${API_BASE_URL}/api/projects/${projectId}/scenes/${sceneId}/keyframes/${keyframeId}/camera-angle`
    : `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/camera-angle`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[changeKeyframeCameraAngle] Camera angle change succeeded');
  return response.json();
}

// ============================================================================
// World Labs 3D World APIs
// ============================================================================

/**
 * Generate (or retrieve cached) 3D world from a keyframe image via World Labs.
 * @param {string} projectId - Project ID
 * @param {string} keyframeId - Keyframe ID
 * @returns {Promise<Object>} { success, worldId, spzUrl, cached }
 */
export async function generateWorldFromKeyframe(projectId, keyframeId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/generate-world`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Send a screenshot from the 3D world explorer to be regenerated as a clean
 * keyframe image via Nano Banana 2.
 * @param {string} projectId - Project ID
 * @param {string} keyframeId - Keyframe ID
 * @param {string} screenshotDataUrl - data: URL from canvas (e.g. image/png) — sent as a file part so large captures are not capped by Starlette’s 1MB form-field limit
 * @param {string} [prompt] - Optional prompt for regeneration
 * @returns {Promise<Object>} Standard keyframe response
 */
export async function captureWorldAngle(projectId, keyframeId, screenshotDataUrl, prompt) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  if (typeof screenshotDataUrl !== 'string' || !screenshotDataUrl.startsWith('data:')) {
    throw new Error('captureWorldAngle: expected a canvas data URL string');
  }
  const resBlob = await fetch(screenshotDataUrl).then((r) => r.blob());
  const ext = screenshotDataUrl.startsWith('data:image/jpeg') || screenshotDataUrl.startsWith('data:image/jpg')
    ? 'jpg'
    : 'png';

  const formData = new FormData();
  formData.append('screenshot', resBlob, `world-capture.${ext}`);
  formData.append('preview', 'true');
  if (prompt) {
    formData.append('prompt', prompt);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/keyframes/${keyframeId}/world-capture`,
    {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

// ============================================================================
// Video-Centric Timeline APIs
// ============================================================================

/**
 * Get all videos for a project, ordered by order_index
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} { success, videos, count }
 */
export async function getProjectVideos(projectId) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[getProjectVideos] Fetching videos for project', projectId);

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[getProjectVideos] Fetch succeeded');
  return response.json();
}

/**
 * Create a project-level video (video-centric timeline).
 * @param {string} projectId
 * @param {Object} params
 * @returns {Promise<Object>} { success, video }
 */
export async function createProjectVideo(projectId, params) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params || {}),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete a project-level video.
 * @param {string} projectId
 * @param {string} videoId
 * @returns {Promise<Object>} { success, deletedId }
 */
export async function deleteProjectVideo(projectId, videoId) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Reorder videos within a project
 * @param {string} projectId - Project ID
 * @param {string[]} videoIds - Array of video IDs in new order
 * @returns {Promise<Object>} { success, message, videoIds }
 */
export async function reorderProjectVideos(projectId, videoIds) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[reorderProjectVideos] Reordering videos', { projectId, count: videoIds.length });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/reorder/bulk`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ videoIds }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[reorderProjectVideos] Reorder succeeded');
  return response.json();
}

/**
 * Update crop values for a video
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID
 * @param {number} cropStartMs - Milliseconds to crop from start
 * @param {number} cropEndMs - Milliseconds to crop from end
 * @returns {Promise<Object>} { success, video }
 */
export async function updateVideoCrop(projectId, videoId, cropStartMs, cropEndMs) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateVideoCrop] Updating video crop', { projectId, videoId, cropStartMs, cropEndMs });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/crop`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ cropStartMs, cropEndMs }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[updateVideoCrop] Update succeeded');
  return response.json();
}

/**
 * Update volume for a video
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID
 * @param {number} volume - Volume level (0-200)
 * @returns {Promise<Object>} { success, video }
 */
export async function updateVideoVolume(projectId, videoId, volume) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[updateVideoVolume] Updating video volume', { projectId, videoId, volume });

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/videos/${videoId}/volume`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ volume }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  console.debug('[updateVideoVolume] Update succeeded');
  return response.json();
}

// ============================================================================
// Video Generation APIs (Standalone video creation)
// ============================================================================

/**
 * Generate a video from text prompt only (no starting image).
 * Creates a container scene with the generated video.
 * 
 * @param {string} projectId - Project ID to add the video to
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Text prompt describing the video
 * @param {string} [params.model='seedance15'] - Model to use
 * @param {string} [params.duration='5'] - Duration in seconds
 * @param {string} [params.aspectRatio='16:9'] - Aspect ratio
 * @param {boolean} [params.generateAudio] - Whether to generate audio
 * @param {string} [params.negativePrompt] - Negative prompt
 * @param {number} [params.cfgScale] - CFG scale 0.0-1.0
 * @param {number} [params.seed] - Random seed
 * @returns {Promise<Object>} { success, scene, video }
 */
export async function generateTextToVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    model = 'seedance15',
    duration = '5',
    aspectRatio = '16:9',
    orderIndex,
    generateAudio,
    addAudio,
    negativePrompt,
    cfgScale,
    seed,
    enableSafetyChecker,
    enablePromptExpansion,
    multiShots,
    resolution,
    fps,
    cameraFixed,
    voiceIds,
    shotType,
    maxRetries = 3,
    initialDelay = 1.0,
  } = params;

  console.debug('[generateTextToVideo] Generating video', {
    projectId,
    promptLength: prompt?.length,
    model,
    duration,
  });

  const body = {
    prompt,
    model,
    duration,
    aspect_ratio: aspectRatio,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  // Add optional parameters if provided
  if (orderIndex !== undefined) body.order_index = orderIndex;
  if (generateAudio !== undefined) body.generate_audio = generateAudio;
  if (addAudio !== undefined) body.add_audio = addAudio;
  if (negativePrompt) body.negative_prompt = negativePrompt;
  if (cfgScale !== undefined) body.cfg_scale = cfgScale;
  if (seed !== undefined) body.seed = seed;
  if (enableSafetyChecker !== undefined) body.enable_safety_checker = enableSafetyChecker;
  if (enablePromptExpansion !== undefined) body.enable_prompt_expansion = enablePromptExpansion;
  if (multiShots !== undefined) body.multi_shots = multiShots;
  if (resolution) body.resolution = resolution;
  if (fps) body.fps = fps;
  if (cameraFixed !== undefined) body.camera_fixed = cameraFixed;
  if (voiceIds && Array.isArray(voiceIds) && voiceIds.length > 0) body.voice_ids = voiceIds;
  if (shotType) body.shot_type = shotType;

  const response = await fetch(`${API_BASE_URL}/api/videos/text-to-video`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateTextToVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateTextToVideo] request succeeded');
  return response.json();
}

/**
 * Re-generate an existing project-level text-to-video with updated params.
 * @param {string} projectId
 * @param {string} videoId
 * @param {Object} params - Same as generateTextToVideo params (prompt/model/duration/etc.)
 */
export async function regenerateTextToVideo(projectId, videoId, params) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    model = 'seedance15',
    duration = '5',
    aspectRatio = '16:9',
    generateAudio,
    addAudio,
    negativePrompt,
    cfgScale,
    seed,
    resolution,
    cameraFixed,
    voiceIds,
    maxRetries = 3,
    initialDelay = 1.0,
  } = params || {};

  const body = {
    prompt,
    model,
    duration,
    aspect_ratio: aspectRatio,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  if (generateAudio !== undefined) body.generate_audio = generateAudio;
  if (addAudio !== undefined) body.add_audio = addAudio;
  if (negativePrompt) body.negative_prompt = negativePrompt;
  if (cfgScale !== undefined) body.cfg_scale = cfgScale;
  if (seed !== undefined) body.seed = seed;
  if (resolution) body.resolution = resolution;
  if (cameraFixed !== undefined) body.camera_fixed = cameraFixed;
  if (voiceIds && Array.isArray(voiceIds) && voiceIds.length > 0) body.voice_ids = voiceIds;

  const response = await fetch(`${API_BASE_URL}/api/videos/text-to-video/${videoId}/regenerate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[regenerateTextToVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[regenerateTextToVideo] request succeeded');
  return response.json();
}

/**
 * Generate a video from an image using various AI models.
 * Creates a container scene with the generated video.
 * 
 * @param {string} projectId - Project ID to add the video to
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Text prompt describing the video
 * @param {string} params.imageUrl - URL of the image to use as first frame
 * @param {string} [params.model='seedance15'] - Model to use
 * @param {string} [params.duration='5'] - Duration in seconds
 * @param {string} [params.aspectRatio] - Aspect ratio (model-specific)
 * @param {string} [params.resolution] - Video resolution (model-specific)
 * @param {boolean} [params.generateAudio] - Whether to generate audio
 * @param {boolean} [params.addAudio=false] - Whether to add audio
 * @returns {Promise<Object>} { success, scene, video }
 */
export async function generateImageToVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    imageUrl,
    model = 'seedance15',
    duration = '5',
    aspectRatio,
    resolution,
    orderIndex,
    generateAudio,
    addAudio = false,
    cameraFixed,
    seed,
    enableSafetyChecker,
    negativePrompt,
    cfgScale,
    enablePromptExpansion,
    multiShots,
    audioUrl,
    voiceIds,
    klingImages,
    fps,
    shotType,
    maxRetries = 3,
    initialDelay = 1.0,
    startKeyframeId, // Video-centric: keyframe ID for elements tracking
  } = params;

  console.debug('[generateImageToVideo] Generating video', {
    projectId,
    promptLength: prompt?.length,
    model,
    duration,
    startKeyframeId,
  });

  const body = {
    prompt,
    image_url: imageUrl,
    model,
    duration,
    add_audio: addAudio,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  // Add optional parameters if provided
  if (orderIndex !== undefined) body.order_index = orderIndex;
  if (aspectRatio) body.aspect_ratio = aspectRatio;
  if (resolution) body.resolution = resolution;
  if (fps) body.fps = fps;
  if (generateAudio !== undefined) body.generate_audio = generateAudio;
  if (cameraFixed !== undefined) body.camera_fixed = cameraFixed;
  if (seed !== undefined) body.seed = seed;
  if (enableSafetyChecker !== undefined) body.enable_safety_checker = enableSafetyChecker;
  if (negativePrompt) body.negative_prompt = negativePrompt;
  if (cfgScale !== undefined) body.cfg_scale = cfgScale;
  if (enablePromptExpansion !== undefined) body.enable_prompt_expansion = enablePromptExpansion;
  if (multiShots !== undefined) body.multi_shots = multiShots;
  if (audioUrl) body.audio_url = audioUrl;
  if (voiceIds && Array.isArray(voiceIds) && voiceIds.length > 0) body.voice_ids = voiceIds;
  if (klingImages && Array.isArray(klingImages) && klingImages.length > 0) body.kling_images = klingImages;
  if (shotType) body.shot_type = shotType;
  if (startKeyframeId) body.start_keyframe_id = startKeyframeId; // Video-centric keyframe tracking

  const response = await fetch(`${API_BASE_URL}/api/videos/image-to-video`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateImageToVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateImageToVideo] request succeeded');
  return response.json();
}

/**
 * Generate a transition video between two images.
 * Creates a container scene with the generated video.
 * 
 * @param {string} projectId - Project ID to add the video to
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Prompt guiding the transition
 * @param {string} params.firstImageUrl - URL of the first frame image
 * @param {string} params.lastImageUrl - URL of the last frame image
 * @param {string} [params.model='seedance15'] - Model to use
 * @param {string} [params.duration='5'] - Duration in seconds
 * @param {string} [params.aspectRatio='auto'] - Aspect ratio
 * @param {string} [params.resolution='480p'] - Video resolution
 * @param {boolean} [params.cameraFixed=false] - Fix camera position (Seedance only)
 * @param {boolean} [params.addAudio=false] - Whether to add audio
 * @returns {Promise<Object>} { success, scene, video }
 */
export async function generateTransitionVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    firstImageUrl,
    lastImageUrl,
    model = 'seedance15',
    duration = '5',
    aspectRatio = 'auto',
    resolution = '480p',
    orderIndex,
    cameraFixed = false,
    addAudio = false,
    generateAudio,
    voiceIds,
    klingImages,
    seed,
    negativePrompt,
    cfgScale,
    shotType,
    maxRetries = 3,
    initialDelay = 1.0,
    startKeyframeId, // Video-centric: keyframe ID for elements tracking
    endKeyframeId,   // Video-centric: keyframe ID for elements tracking
  } = params;

  console.debug('[generateTransitionVideo] Generating transition', {
    projectId,
    promptLength: prompt?.length,
    model,
    duration,
    startKeyframeId,
    endKeyframeId,
  });

  const body = {
    prompt,
    first_image_url: firstImageUrl,
    last_image_url: lastImageUrl,
    model,
    duration,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  // Add optional parameters if provided
  if (orderIndex !== undefined) body.order_index = orderIndex;
  if (aspectRatio) body.aspect_ratio = aspectRatio;
  if (resolution) body.resolution = resolution;
  if (cameraFixed !== undefined) body.camera_fixed = cameraFixed;
  if (addAudio !== undefined) body.add_audio = addAudio;
  if (seed !== undefined) body.seed = seed;
  if (negativePrompt) body.negative_prompt = negativePrompt;
  if (cfgScale !== undefined) body.cfg_scale = cfgScale;
  if (shotType) body.shot_type = shotType;
  if (generateAudio !== undefined) body.generate_audio = generateAudio;
  if (voiceIds && Array.isArray(voiceIds) && voiceIds.length > 0) body.voice_ids = voiceIds;
  if (klingImages && Array.isArray(klingImages) && klingImages.length > 0) body.kling_images = klingImages;
  if (startKeyframeId) body.start_keyframe_id = startKeyframeId; // Video-centric keyframe tracking
  if (endKeyframeId) body.end_keyframe_id = endKeyframeId; // Video-centric keyframe tracking

  const response = await fetch(`${API_BASE_URL}/api/videos/transition`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateTransitionVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateTransitionVideo] request succeeded');
  return response.json();
}

/**
 * Re-generate an existing project-level transition video with updated params.
 * @param {string} videoId
 * @param {Object} params
 * @param {string} params.projectId
 * @param {string} params.prompt
 * @param {string} params.firstImageUrl
 * @param {string} params.lastImageUrl
 * @param {string} [params.model='seedance15']
 * @param {string} [params.duration='5']
 * @param {string} [params.resolution]
 * @param {boolean} [params.keepAudio=true]
 * @param {boolean} [params.addAudio]
 * @param {boolean} [params.cameraFixed]
 * @param {number} [params.maxRetries=3]
 * @param {number} [params.initialDelay=1.0]
 */
export async function regenerateTransitionVideo(videoId, params) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    projectId,
    prompt,
    firstImageUrl,
    lastImageUrl,
    model = 'seedance15',
    duration = '5',
    resolution,
    addAudio,
    cameraFixed,
    klingImages,
    aspectRatio,
    maxRetries = 3,
    initialDelay = 1.0,
  } = params || {};

  const body = {
    prompt,
    first_image_url: firstImageUrl,
    last_image_url: lastImageUrl,
    model,
    duration,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  if (resolution) body.resolution = resolution;
  if (addAudio !== undefined) body.add_audio = addAudio;
  if (cameraFixed !== undefined) body.camera_fixed = cameraFixed;
  if (klingImages && Array.isArray(klingImages) && klingImages.length > 0) body.kling_images = klingImages;
  if (aspectRatio) body.aspect_ratio = aspectRatio;

  const response = await fetch(`${API_BASE_URL}/api/videos/transition/${videoId}/regenerate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[regenerateTransitionVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[regenerateTransitionVideo] request succeeded');
  return response.json();
}

// ============================================================================
// Video Model Specs
// ============================================================================

let _videoModelSpecsCache = null;
let _videoModelSpecsInFlight = null;
// Keep this short: model rows are deployed independently through SQL migrations.
// Bump the storage key when catalog semantics change so old deployments cannot
// hide newly activated models for the previous 24-hour cache window.
const VIDEO_MODEL_SPECS_STORAGE_KEY = 'bookthing-video-model-specs-v2';
const VIDEO_MODEL_SPECS_CACHE_TTL_MS = 2 * 60 * 1000;

function readPersistedVideoModelSpecs() {
  if (typeof window === 'undefined') return null;
  try {
    const cached = JSON.parse(window.localStorage.getItem(VIDEO_MODEL_SPECS_STORAGE_KEY));
    if (!cached?.value || !Number.isFinite(cached.cachedAt)) return null;
    if (Date.now() - cached.cachedAt > VIDEO_MODEL_SPECS_CACHE_TTL_MS) return null;
    return cached.value;
  } catch (_) {
    return null;
  }
}

function persistVideoModelSpecs(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIDEO_MODEL_SPECS_STORAGE_KEY, JSON.stringify({
      cachedAt: Date.now(),
      value,
    }));
  } catch (_) {
    // Model specs still remain available in the in-memory cache.
  }
}

/** Return model specs synchronously when memory/local storage already has them. */
export function getCachedVideoModelSpecs() {
  if (_videoModelSpecsCache) return _videoModelSpecsCache;
  _videoModelSpecsCache = readPersistedVideoModelSpecs();
  return _videoModelSpecsCache;
}

/**
 * Fetch available video models + their supported specs (e.g., valid durations).
 * This intentionally moves model specs out of the frontend.
 */
export async function getVideoModelSpecs({ force = false } = {}) {
  if (!force) {
    const cached = getCachedVideoModelSpecs();
    if (cached) return cached;
  }
  // A forced refresh should still share a request already in flight.
  if (_videoModelSpecsInFlight) return _videoModelSpecsInFlight;

  _videoModelSpecsInFlight = (async () => {
    const token = await getAuthToken();

    const modelSpecsUrl = `${API_BASE_URL}/api/videos/model-specs${force ? '?refresh=true' : ''}`;
    const response = await fetch(modelSpecsUrl, {
      method: 'GET',
      // `force` must bypass both our local cache and the browser HTTP cache.
      // Model migrations can change this response while the frontend remains
      // open, so relying only on the in-memory/localStorage bypass is stale.
      cache: force ? 'no-store' : 'default',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw createApiError(response, error);
    }

    const json = await response.json();
    _videoModelSpecsCache = json;
    persistVideoModelSpecs(json);
    return json;
  })().finally(() => {
    _videoModelSpecsInFlight = null;
  });

  return _videoModelSpecsInFlight;
}

/**
 * Generate a video using Kling O1 Reference Image to Video.
 * Creates a container scene with the generated video.
 * 
 * This uses reference images and elements to generate videos with 
 * consistent characters/objects. Use @Image1, @Image2... and 
 * @Element1, @Element2... references in the prompt.
 * 
 * @param {string} projectId - Project ID to add the video to
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Prompt with @Image1, @Image2... and @Element1, @Element2... references
 * @param {string[]} [params.imageUrls] - List of reference image URLs
 * @param {Object[]} [params.elements] - List of element dicts (characters/objects)
 * @param {string} [params.duration='5'] - Duration ('5' or '10')
 * @param {string} [params.aspectRatio='16:9'] - Aspect ratio ('16:9', '9:16', '1:1')
 * @returns {Promise<Object>} { success, scene, video }
 */
export async function generateReferenceVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    imageUrls,
    elements,
    duration = '5',
    aspectRatio = '16:9',
    orderIndex,
    maxRetries = 3,
    initialDelay = 1.0,
  } = params;

  console.debug('[generateReferenceVideo] Generating reference video', {
    projectId,
    promptLength: prompt?.length,
    imageUrlCount: imageUrls?.length || 0,
    elementCount: elements?.length || 0,
  });

  const body = {
    prompt,
    duration,
    aspect_ratio: aspectRatio,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  // Add optional parameters if provided
  if (orderIndex !== undefined) body.order_index = orderIndex;
  if (imageUrls && imageUrls.length > 0) body.image_urls = imageUrls;
  if (elements && elements.length > 0) body.elements = elements;

  const response = await fetch(`${API_BASE_URL}/api/videos/reference`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateReferenceVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateReferenceVideo] request succeeded');
  return response.json();
}

/**
 * Generate a standalone Seedance 2.0 or Seedance 2.5 video.
 *
 * @param {string} projectId
 * @param {Object} params
 * @param {string} params.prompt
 * @param {string} [params.model='seedance_2.0_fast'] - Seedance model alias, including `seedance25-ref`, `seedance25-i2v`, or `seedance25-t2v`
 * @param {string} [params.functionMode='omni_reference'] - `omni_reference` or `first_last_frames`
 * @param {string} [params.ratio='16:9']
 * @param {number|string} [params.duration=5]
 * @param {string[]} [params.imageFiles]
 * @param {string[]} [params.videoFiles]
 * @param {string[]} [params.audioFiles]
 * @param {string[]} [params.filePaths]
 * @param {string[]} [params.referenceKeyframeIds]
 * @param {string[]} [params.referenceVideoIds]
 * @param {string[]} [params.voiceIds]
 * @returns {Promise<Object>} { success, video }
 */
export async function generateSeedance2Video(projectId, params) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    model = 'seedance_2.0_fast',
    functionMode = 'omni_reference',
    ratio = '16:9',
    duration = 5,
    resolution,
    generateAudio,
    seed,
    watermark,
    returnLastFrame,
    enableSafetyChecker,
    promptExpansionMode,
    enablePromptExpansion,
    enableThinking,
    imageFiles,
    videoFiles,
    audioFiles,
    filePaths,
    referenceKeyframeIds,
    referenceVideoIds,
    voiceIds,
    orderIndex,
    maxRetries = 2,
    initialDelay = 2.0,
    pollInterval = 10,
    pollTimeout = 3600,
    pollRequestRetries = 10,
    pollRequestRetryDelay = 2.0,
    uploadToCdn = true,
  } = params || {};

  const body = {
    prompt,
    model,
    function_mode: functionMode,
    ratio,
    duration,
    max_retries: maxRetries,
    initial_delay: initialDelay,
    poll_interval: pollInterval,
    poll_timeout: pollTimeout,
    poll_request_retries: pollRequestRetries,
    poll_request_retry_delay: pollRequestRetryDelay,
    upload_to_cdn: uploadToCdn,
  };
  if (projectId) body.project_id = projectId;

  if (resolution) body.resolution = resolution;
  if (generateAudio !== undefined) body.generate_audio = generateAudio;
  if (seed !== undefined) body.seed = seed;
  if (watermark !== undefined) body.watermark = watermark;
  if (returnLastFrame !== undefined) body.return_last_frame = returnLastFrame;
  if (enableSafetyChecker !== undefined) body.enable_safety_checker = enableSafetyChecker;
  if (promptExpansionMode) body.prompt_expansion_mode = promptExpansionMode;
  if (enablePromptExpansion !== undefined) body.enable_prompt_expansion = enablePromptExpansion;
  if (enableThinking !== undefined) body.enable_thinking = enableThinking;

  if (orderIndex !== undefined) body.order_index = orderIndex;
  if (imageFiles?.length) body.image_files = imageFiles;
  if (videoFiles?.length) body.video_files = videoFiles;
  if (audioFiles?.length) body.audio_files = audioFiles;
  if (filePaths?.length) body.file_paths = filePaths;
  if (referenceKeyframeIds?.length) body.reference_keyframe_ids = referenceKeyframeIds;
  if (referenceVideoIds?.length) body.reference_video_ids = referenceVideoIds;
  if (voiceIds?.length) body.voice_ids = voiceIds;

  const response = await fetch(`${API_BASE_URL}/api/videos/seedance-2`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateSeedance2Video] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Upload a reference video file for motion control video generation.
 * 
 * @param {File} videoFile - Video file to upload (mp4, webm, mov)
 * @returns {Promise<Object>} { success, video_url: string }
 */
export async function uploadReferenceVideo(videoFile, projectId = null, persistRecord = false) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[uploadReferenceVideo] Uploading reference video', {
    fileName: videoFile.name,
    fileSize: videoFile.size,
    fileType: videoFile.type,
  });

  const formData = new FormData();
  formData.append('video_file', videoFile);
  if (projectId) formData.append('project_id', projectId);
  if (persistRecord) formData.append('persist_record', 'true');

  const response = await fetch(`${API_BASE_URL}/api/videos/upload-reference-video`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[uploadReferenceVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[uploadReferenceVideo] request succeeded');
  return response.json();
}

/**
 * Upload a source video file for video-to-video generation.
 *
 * @param {File} videoFile - Video file to upload (mp4, webm, mov)
 * @returns {Promise<Object>} { success, video_url: string }
 */
export async function uploadSourceVideo(videoFile, projectId = null) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[uploadSourceVideo] Uploading source video', {
    fileName: videoFile.name,
    fileSize: videoFile.size,
    fileType: videoFile.type,
  });

  const formData = new FormData();
  formData.append('video_file', videoFile);
  if (projectId) formData.append('project_id', projectId);

  const response = await fetch(`${API_BASE_URL}/api/videos/upload-source-video`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[uploadSourceVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[uploadSourceVideo] request succeeded');
  return response.json();
}

/**
 * Upload a source audio file for Seedance 2 reference generation.
 *
 * @param {File} audioFile - Audio file to upload (mp3, wav, m4a, aac, ogg, webm)
 * @param {string|null} [projectId] - Project ID used to persist the uploaded audio clip
 * @returns {Promise<Object>} { success, audio_url: string, audioClipId?: string, audioClip?: Object }
 */
export async function uploadSourceAudio(audioFile, projectId = null) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  console.debug('[uploadSourceAudio] Uploading source audio', {
    fileName: audioFile.name,
    fileSize: audioFile.size,
    fileType: audioFile.type,
  });

  const formData = new FormData();
  formData.append('audio_file', audioFile);
  if (projectId) formData.append('project_id', projectId);

  const response = await fetch(`${API_BASE_URL}/api/videos/upload-source-audio`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[uploadSourceAudio] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[uploadSourceAudio] request succeeded');
  return response.json();
}

/**
 * Generate a motion control video using Kling Motion Control models.
 * 
 * Transfers motion from a reference video to a character image, creating a new video
 * where the character performs the movements from the reference.
 * 
 * @param {string} projectId - Project ID to add the video to
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Text prompt describing the desired output
 * @param {string} params.imageUrl - URL of the character image to animate
 * @param {string} params.videoUrl - URL of the reference video (3-30 seconds)
 * @param {string} [params.model='kling26mc'] - Model: 'kling26mc', 'kling26mcpro', 'kling3mc', or 'kling3mcpro'
 * @param {string} [params.characterOrientation='image'] - 'image' or 'video'
 * @param {boolean} [params.keepOriginalSound=false] - Keep reference video audio
 * @param {string} [params.duration='5'] - Duration ('5' or '10')
 * @param {string} [params.negativePrompt] - Elements to avoid
 * @param {number} [params.orderIndex] - Position in timeline
 * @param {string} [params.startKeyframeId] - ID of keyframe used as character image
 * @param {string} [params.videoId] - Existing video ID to regenerate in place
 * @returns {Promise<Object>} { success, video }
 */
export async function generateMotionControlVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    imageUrl,
    videoUrl,
    model = 'kling26mc',
    characterOrientation = 'image',
    keepOriginalSound = false,
    duration = '5',
    negativePrompt,
    orderIndex,
    startKeyframeId,
    videoId,
    maxRetries = 3,
    initialDelay = 1.0,
  } = params;

  console.debug('[generateMotionControlVideo] Generating motion control video', {
    projectId,
    promptLength: prompt?.length,
    model,
    characterOrientation,
    keepOriginalSound,
    duration,
    startKeyframeId,
  });

  const body = {
    prompt,
    image_url: imageUrl,
    video_url: videoUrl,
    model,
    character_orientation: characterOrientation,
    keep_original_sound: keepOriginalSound,
    duration,
    max_retries: maxRetries,
    initial_delay: initialDelay,
  };
  if (projectId) body.project_id = projectId;

  // Add optional parameters if provided
  if (orderIndex !== undefined) body.order_index = orderIndex;
  if (negativePrompt) body.negative_prompt = negativePrompt;
  if (startKeyframeId) body.start_keyframe_id = startKeyframeId;
  if (videoId) body.video_id = videoId;

  const response = await fetch(`${API_BASE_URL}/api/videos/motion-control`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateMotionControlVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateMotionControlVideo] request succeeded');
  return response.json();
}

/**
 * Generate an avatar (talking-head) video using VEED Fabric 1.0.
 * Turns a portrait/face image into a lip-synced talking video.
 * @param {string} projectId - Project ID
 * @param {Object} params - Generation parameters
 * @param {string} [params.imageUrl] - URL of the avatar/face image
 * @param {string} [params.startKeyframeId] - Keyframe ID to use as avatar image
 * @param {string} [params.voiceoverText] - Script text for TTS generation
 * @param {string} [params.audioUrl] - URL of pre-recorded audio for lip-sync
 * @param {string} [params.voiceId] - Voice ID for TTS (project voice record)
 * @param {string} [params.model='standard'] - Model variant: "standard" or "fast"
 * @param {string} [params.resolution='720p'] - Output resolution: "480p" or "720p"
 * @param {string} [params.videoId] - Existing video ID to regenerate
 * @param {number} [params.orderIndex] - Position in timeline
 * @returns {Promise<Object>} Response with video data
 */
export async function generateAvatarVideo(projectId, params) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    imageUrl,
    startKeyframeId,
    voiceoverText,
    audioUrl,
    voiceId,
    model = 'standard',
    resolution = '720p',
    videoId,
    orderIndex,
  } = params;

  console.debug('[generateAvatarVideo] Generating avatar video', {
    projectId,
    model,
    resolution,
    hasVoiceoverText: !!voiceoverText,
    hasAudioUrl: !!audioUrl,
    startKeyframeId,
  });

  const body = {
    project_id: projectId,
    model,
    resolution,
  };

  if (imageUrl) body.image_url = imageUrl;
  if (startKeyframeId) body.start_keyframe_id = startKeyframeId;
  if (voiceoverText) body.voiceover_text = voiceoverText;
  if (audioUrl) body.audio_url = audioUrl;
  if (voiceId) body.voice_id = voiceId;
  if (videoId) body.video_id = videoId;
  if (orderIndex !== undefined) body.order_index = orderIndex;

  const response = await fetch(`${API_BASE_URL}/api/videos/avatar`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateAvatarVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateAvatarVideo] request succeeded');
  return response.json();
}

/**
 * Generate a motion graphics video using Remotion
 * Creates animated explainer-style videos with text, icons, and visual elements
 * @param {string} projectId - Project ID
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Description of the motion graphic to generate
 * @param {string} [params.duration='5'] - Duration in seconds
 * @param {string} [params.aspectRatio] - Aspect ratio (defaults to project's)
 * @param {number} [params.orderIndex] - Position in timeline
 * @param {string} [params.voiceover=''] - Voiceover script to sync animations with
 * @param {File[]} [params.attachments=[]] - Image/video files to include
 * @returns {Promise<Object>} Response with video data
 */
export async function generateMotionGraphicsVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    duration = '5',
    aspectRatio,
    orderIndex,
    voiceover = '',
    attachments = [],
    isOverlay = false,
    overlayStartMs,
    overlayVideoId,
  } = params;

  console.debug('[generateMotionGraphicsVideo] Generating motion graphics video', {
    projectId,
    promptLength: prompt?.length,
    duration,
    aspectRatio,
    voiceoverLength: voiceover?.length || 0,
    attachmentCount: attachments?.length || 0,
  });

  // Use FormData to support file uploads
  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('prompt', prompt);
  // Handle 'Auto' duration - send empty string or null, backend will determine automatically
  if (duration !== null && duration !== undefined) {
    formData.append('duration', duration);
  }
  formData.append('voiceover', voiceover || '');

  // Add optional parameters if provided
  if (aspectRatio) formData.append('aspect_ratio', aspectRatio);
  if (orderIndex !== undefined) formData.append('order_index', orderIndex.toString());
  if (isOverlay) formData.append('is_overlay', 'true');
  if (overlayStartMs !== undefined) formData.append('overlay_start_ms', overlayStartMs.toString());
  if (overlayVideoId) formData.append('overlay_video_id', overlayVideoId);

  // Add attachments
  if (attachments && attachments.length > 0) {
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });
  }

  const response = await fetch(`${API_BASE_URL}/api/videos/motion-graphics`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // Don't set Content-Type - let browser set it with boundary for FormData
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateMotionGraphicsVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateMotionGraphicsVideo] request succeeded');
  return response.json();
}

/**
 * Re-generate an existing motion graphics video in place (same video entity).
 * @param {string} projectId - Project ID
 * @param {string} videoId - Existing motion graphics video ID
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Description of the motion graphic to generate
 * @param {string} [params.duration='Auto'] - Duration in seconds or 'Auto'
 * @param {string} [params.aspectRatio] - Aspect ratio (defaults to project's)
 * @param {string} [params.voiceover=''] - Voiceover script
 * @param {File[]} [params.attachments=[]] - Optional attachments
 * @returns {Promise<Object>} Response with updated video data
 */
export async function regenerateMotionGraphicsVideo(projectId, videoId, params) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    duration = 'Auto',
    aspectRatio,
    voiceover = '',
    attachments = [],
  } = params || {};

  console.debug('[regenerateMotionGraphicsVideo] Regenerating motion graphics video', {
    projectId,
    videoId,
    promptLength: prompt?.length,
    duration,
    aspectRatio,
    voiceoverLength: voiceover?.length || 0,
    attachmentCount: attachments?.length || 0,
  });

  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('prompt', prompt);
  if (duration !== null && duration !== undefined) {
    formData.append('duration', duration);
  }
  formData.append('voiceover', voiceover || '');
  if (aspectRatio) formData.append('aspect_ratio', aspectRatio);

  if (attachments && attachments.length > 0) {
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });
  }

  const response = await fetch(`${API_BASE_URL}/api/videos/motion-graphics/${videoId}/regenerate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[regenerateMotionGraphicsVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[regenerateMotionGraphicsVideo] request succeeded');
  return response.json();
}

/**
 * Generate multiple AI videos from a single prompt using the AI video generation pipeline.
 * Videos are created in the database and streamed to the frontend via Supabase Realtime.
 * 
 * @param {string} projectId - Project ID
 * @param {Object} params - Generation parameters
 * @param {string} params.prompt - Description of the video sequence to create
 * @param {string} [params.duration='10'] - Total duration in seconds (5-60)
 * @param {string} [params.aspectRatio] - Aspect ratio (e.g., '16:9', '9:16')
 * @param {number} [params.orderIndex] - Order index for timeline placement
 * @returns {Promise<Object>} Response with scene and video IDs
 */
export async function generateMultiShotVideo(projectId, params) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  const {
    prompt,
    duration = '10',
    aspectRatio,
    orderIndex,
  } = params;

  console.debug('[generateMultiShotVideo] Generating multi-shot video', {
    projectId,
    promptLength: prompt?.length,
    duration,
    aspectRatio,
  });

  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('prompt', prompt);
  formData.append('duration', duration);
  if (aspectRatio) formData.append('aspect_ratio', aspectRatio);
  if (orderIndex !== undefined) formData.append('order_index', orderIndex.toString());

  const response = await fetch(`${API_BASE_URL}/api/videos/multi-shot`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[generateMultiShotVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[generateMultiShotVideo] request succeeded');
  return response.json();
}

/**
 * Edit a motion graphics video by modifying its Remotion code via AI
 * @param {string} projectId - Project ID
 * @param {string} videoId - Video ID to edit
 * @param {string} prompt - Edit instruction (e.g., "Make the title bigger")
 * @param {Array} [chatHistory] - Previous chat messages for context
 * @param {File[]} [attachments=[]] - Image/video files to include
 * @returns {Promise<Object>} Response with updated video data
 */
export async function editMotionGraphicsVideo(projectId, videoId, prompt, chatHistory = [], attachments = []) {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Allow sending attachments without prompt text
  if ((!prompt || !prompt.trim()) && (!attachments || attachments.length === 0)) {
    throw new Error('Edit prompt or attachments are required');
  }

  console.debug('[editMotionGraphicsVideo] Editing motion graphics video', {
    projectId,
    videoId,
    promptLength: prompt?.length,
    chatHistoryLength: chatHistory?.length || 0,
    attachmentCount: attachments?.length || 0,
  });

  // Use FormData to support file uploads
  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('prompt', (prompt || '').trim());

  // Include chat history as JSON string
  if (chatHistory && chatHistory.length > 0) {
    const historyForApi = chatHistory.map(msg => ({
      role: msg.role,
      content: msg.content,
    }));
    formData.append('chat_history', JSON.stringify(historyForApi));
  }

  // Add attachments
  if (attachments && attachments.length > 0) {
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });
  }

  const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}/edit-motion-graphics`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // Don't set Content-Type - let browser set it with boundary for FormData
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[editMotionGraphicsVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }

  console.debug('[editMotionGraphicsVideo] request succeeded');
  return response.json();
}

/**
 * Enhance a text prompt for image generation.
 */
export async function enhanceImagePrompt({ text, projectId, style, mood }) {
  if (!text || !text.trim()) {
    throw new Error('Prompt text is required');
  }

  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  const payload = { text: text.trim() };
  if (projectId) payload.project_id = projectId;
  if (style) payload.style = style;
  if (mood) payload.mood = mood;

  const response = await fetch(`${API_BASE_URL}/api/prompt-enhancement/image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }

  return response.json();
}

/**
 * Enhance a text prompt for video generation.
 */
export async function enhanceVideoPrompt({ text, projectId, duration, cameraMovement, style, imageUrls, videoContext }) {
  if (!text || !text.trim()) {
    throw new Error('Prompt text is required');
  }

  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  const payload = { text: text.trim() };
  if (projectId) payload.project_id = projectId;
  if (Number.isFinite(duration)) payload.duration = duration;
  if (cameraMovement) payload.camera_movement = cameraMovement;
  if (style) payload.style = style;
  if (Array.isArray(imageUrls) && imageUrls.length > 0) payload.image_urls = imageUrls.filter(Boolean);
  if (videoContext) payload.video_context = videoContext;

  const response = await fetch(`${API_BASE_URL}/api/prompt-enhancement/video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }

  return response.json();
}

// -- UGC Ideas Generation --

/**
 * Generate UGC video script ideas using AI.
 * @param {Object} params
 * @param {string} params.description - Product description
 * @param {number} [params.numIdeas] - Number of ideas to generate (1-10)
 * @param {string} [params.characterImageUrl] - CDN URL for default character
 * @param {string} [params.characterName] - Name of the default character
 * @param {File} [params.characterFile] - Uploaded character image file
 * @param {string[]} [params.productImageUrls] - Existing product image URLs
 * @param {File[]} [params.productFiles] - Uploaded product image files
 * @returns {Promise<{ ideas: Array<{ title: string, detailed_script: string }> }>}
 */
export async function generateUgcIdeas({ description, numIdeas, characterImageUrl, characterName, characterFile, productImageUrls, productFiles }) {
  const formData = new FormData();
  formData.append('description', description);
  formData.append('num_ideas', String(numIdeas || 3));

  if (characterImageUrl) formData.append('character_image_url', characterImageUrl);
  if (characterName) formData.append('character_name', characterName);
  if (characterFile) formData.append('character_file', characterFile);
  for (const imageUrl of (productImageUrls || [])) {
    if (imageUrl) formData.append('product_image_urls', imageUrl);
  }

  for (const file of (productFiles || [])) {
    formData.append('product_files', file);
  }

  const response = await fetch(`${API_BASE_URL}/api/ugc/generate-ideas`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Generate replacement opening hooks from a source video.
 * @param {Object} params
 * @param {File} params.videoFile - Source video to analyze
 * @param {number} [params.numHooks] - Number of hooks to generate (1-10)
 * @returns {Promise<{ videoUrl: string, hooks: Array<{ title: string, strategy?: string, hookScript: string }> }>}
 */
export async function generateHookScripts({ videoFile, numHooks }) {
  const formData = new FormData();
  formData.append('video_file', videoFile);
  formData.append('num_hooks', String(numHooks || 3));

  const response = await fetch(`${API_BASE_URL}/api/hook-changer/generate-hooks`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }

  const data = await response.json();
  return {
    videoUrl: data.video_url || data.videoUrl || '',
    hooks: (data.hooks || []).map((hook) => ({
      title: hook.title || 'Hook',
      strategy: hook.strategy || '',
      hookScript: hook.hook_script || hook.hookScript || '',
    })),
  };
}

/**
 * Generate video template variants from a source video.
 * @param {Object} params
 * @param {string} params.templateId - Template id (`change-avatar`, `change-style`, `change-cta`)
 * @param {File} params.videoFile - Source video to analyze
 * @param {number} [params.numVariants] - Number of variants to generate (1-10)
 * @returns {Promise<{ templateId: string, videoUrl: string, variants: Array<{ title: string, strategy?: string, variantInstruction: string }> }>}
 */
export async function generateTemplateVariants({ templateId, videoFile, numVariants }) {
  const formData = new FormData();
  formData.append('template_id', templateId);
  formData.append('video_file', videoFile);
  formData.append('num_variants', String(numVariants || 3));

  const response = await fetch(`${API_BASE_URL}/api/video-template-variants/generate`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }

  const data = await response.json();
  return {
    templateId: data.template_id || data.templateId || templateId,
    videoUrl: data.video_url || data.videoUrl || '',
    variants: (data.variants || []).map((variant) => ({
      title: variant.title || 'Variant',
      strategy: variant.strategy || '',
      variantInstruction: variant.variant_instruction || variant.variantInstruction || '',
    })),
  };
}

// -- Agent Video Composer API --

/**
 * Start an agent video composition run for a project.
 * @param {string} projectId - Public project ID
 * @param {string} prompt - User prompt
 * @param {File[]} [files] - Optional user attachments (images, videos, audio)
 * @param {string} [modelTier] - Tier selector (`fast`, `pro` or `max`)
 * @param {string[]} [attachmentUrls] - Optional public attachment URLs
 * @param {string|null} [source] - Optional source tag for backend policy handling
 * @param {Object} [options] - Optional compose metadata
 * @param {string|null} [options.videoId] - Existing video id for edit runs
 * @param {boolean} [options.analyzeVideoAttachments] - Deconstruct attached videos as hidden agent context
 * @param {boolean} [options.extraCareful] - Inspect generated video outputs and retry visible mistakes
 * @param {Array<{kind: 'space'|'space_item'|'character'|'voice'|'image'|'video', id: string}>} [options.attachmentReferences] - Server-resolved library references
 * @param {string} [options.displayPrompt] - User-visible prompt when `prompt` includes hidden agent context
 * @returns {Promise<{ run_id: string, status: string }>}
 */
export async function getComposerMediaModels() {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/agent/media-models`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

async function composerMediaPresetRequest(path = '', options = {}) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/agent/media-model-presets${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.status === 204 ? null : response.json();
}

export const listComposerMediaModelPresets = () => composerMediaPresetRequest();
export const createComposerMediaModelPreset = (preset) => composerMediaPresetRequest('', { method: 'POST', body: JSON.stringify(preset) });
export const updateComposerMediaModelPreset = (presetId, preset) => composerMediaPresetRequest(`/${encodeURIComponent(presetId)}`, { method: 'PUT', body: JSON.stringify(preset) });
export const deleteComposerMediaModelPreset = (presetId) => composerMediaPresetRequest(`/${encodeURIComponent(presetId)}`, { method: 'DELETE' });

export async function startAgentCompose(projectId, prompt, files = [], modelTier = 'pro', attachmentUrls = [], source = null, options = {}) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });
  if (!token) throw new Error('Authentication required');

  const formData = new FormData();
  formData.append('project_id', projectId);
  formData.append('prompt', prompt);
  formData.append('model_tier', modelTier);
  if (typeof options?.displayPrompt === 'string') {
    formData.append('display_prompt', options.displayPrompt);
  }

  for (const file of files) {
    formData.append('files', file);
  }

  if (attachmentUrls.length > 0) {
    formData.append('attachment_urls', JSON.stringify(attachmentUrls));
  }
  if (Array.isArray(options?.attachmentReferences) && options.attachmentReferences.length > 0) {
    formData.append('attachment_references', JSON.stringify(options.attachmentReferences));
  }
  if (source) {
    formData.append('source', source);
  }
  // Continue the conversation this prompt was typed into instead of opening a
  // new one; the backend falls back to a fresh session when it is absent.
  if (options?.sessionId) {
    formData.append('session_id', options.sessionId);
  }
  if (options?.videoId) {
    formData.append('video_id', options.videoId);
  }
  if (options?.analyzeVideoAttachments) {
    formData.append('analyze_video_attachments', 'true');
  }
  if (options?.extraCareful) {
    formData.append('extra_careful', 'true');
  }
  if (options?.llmModel) {
    formData.append('llm_model', options.llmModel);
  }
  if (options?.defaultVideoModel) {
    formData.append('default_video_model', options.defaultVideoModel);
  }
  if (Array.isArray(options?.availableVideoModels) && options.availableVideoModels.length > 0) {
    formData.append('available_video_models', JSON.stringify(options.availableVideoModels));
  }
  if (options?.defaultImageModel) {
    formData.append('default_image_model', options.defaultImageModel);
  }
  if (Array.isArray(options?.availableImageModels) && options.availableImageModels.length > 0) {
    formData.append('available_image_models', JSON.stringify(options.availableImageModels));
  }
  // Audit only. The server ignores an id the caller does not own, and always
  // executes the validated four-field selection above.
  if (options?.mediaModelPresetId) {
    formData.append('media_model_preset_id', options.mediaModelPresetId);
  }
  if (options?.mediaModelSelectionSource) {
    formData.append('media_model_selection_source', options.mediaModelSelectionSource);
  }

  const response = await fetch(`${API_BASE_URL}/api/agent/compose`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/** Plan and launch one or more independent Composer projects. */
export async function startMultiAgentCompose(prompt, files = [], attachmentUrls = [], options = {}) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });
  if (!token) throw new Error('Authentication required');
  const formData = new FormData();
  formData.append('prompt', prompt);
  formData.append('display_prompt', options.displayPrompt ?? prompt);
  formData.append('aspect_ratio', options.aspectRatio || 'auto');
  formData.append('llm_model', options.llmModel || 'openai/gpt-5.6-sol');
  if (options.extraCareful) formData.append('extra_careful', 'true');
  files.forEach((file) => formData.append('files', file));
  if (attachmentUrls.length) formData.append('attachment_urls', JSON.stringify(attachmentUrls));
  if (options.attachmentReferences?.length) {
    formData.append('attachment_references', JSON.stringify(options.attachmentReferences));
  }
  if (options.defaultVideoModel) formData.append('default_video_model', options.defaultVideoModel);
  if (options.availableVideoModels?.length) formData.append('available_video_models', JSON.stringify(options.availableVideoModels));
  if (options.defaultImageModel) formData.append('default_image_model', options.defaultImageModel);
  if (options.availableImageModels?.length) formData.append('available_image_models', JSON.stringify(options.availableImageModels));
  if (options.mediaModelPresetId) formData.append('media_model_preset_id', options.mediaModelPresetId);
  if (options.mediaModelSelectionSource) formData.append('media_model_selection_source', options.mediaModelSelectionSource);
  const response = await fetch(`${API_BASE_URL}/api/agent/multi-compose`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

export async function getMultiAgentGroup(projectId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/agent/multi-groups/project/${encodeURIComponent(projectId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

export async function retryMultiAgentMember(groupId, projectId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/agent/multi-groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(projectId)}/retry`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Get the latest agent run for a project.
 * @param {string} projectId - Public project ID
 * @returns {Promise<Object|null>}
 */
export async function getAgentRunByProject(projectId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/agent/run/project/${projectId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 404) return null;
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/** List composer runs for a project, newest first. */
export async function getAgentRunsByProject(projectId, limit = 30) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(
    `${API_BASE_URL}/api/agent/runs/project/${projectId}?limit=${encodeURIComponent(limit)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Get every turn of a composer conversation with the events each one produced.
 * @param {string} sessionId - Composer session UUID
 * @param {string} projectId - Public project ID that owns the session
 * @returns {Promise<{ session_id: string, runs: Array, events: Array }>}
 */
export async function getAgentSession(sessionId, projectId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(
    `${API_BASE_URL}/api/agent/session/${sessionId}?project_id=${encodeURIComponent(projectId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Get all events for an agent run (hydration on page refresh).
 * @param {string} runId - Agent run UUID
 * @param {number|null} [afterSeq] - Incremental cursor; updated older rows are replayed
 * @returns {Promise<Array>}
 */
export async function getAgentEvents(runId, afterSeq = null) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const params = new URLSearchParams();
  if (Number.isFinite(afterSeq)) params.set('after_seq', String(afterSeq));
  const query = params.toString();
  const response = await fetch(`${API_BASE_URL}/api/agent/events/${runId}${query ? `?${query}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Legacy stripped event cursor. Composer's visible work log uses getAgentEvents
 * so tool names, arguments, results, and explicit reasoning remain available.
 */
export async function getAgentSyncEvents(runId, afterSeq = null) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const params = new URLSearchParams({ sync_safe: 'true' });
  if (Number.isFinite(afterSeq)) params.set('after_seq', String(afterSeq));
  const response = await fetch(`${API_BASE_URL}/api/agent/events/${runId}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Get the safe, video-domain progress projection for a Composer run.
 * Raw tool names, source operations, commands, and paths are never returned.
 */
export async function getAgentProgress(runId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/agent/progress/${runId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Get durable Composer media jobs for a project/session.
 * @param {string} projectId - Public project ID
 * @param {string|null} sessionId - Optional Composer conversation UUID
 * @returns {Promise<Array>}
 */
export async function getComposerMediaJobs(projectId, sessionId = null) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const params = new URLSearchParams();
  if (sessionId) params.set('session_id', sessionId);
  const query = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/agent/media-jobs/project/${projectId}${query ? `?${query}` : ''}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Get whether the current user can start agentic project creation.
 * @returns {Promise<{allowed:boolean, reason:string|null, billing_mode:string, is_first_project:boolean, auto_reload_enabled:boolean, billed_to_user_id:string|null}>}
 */
export async function getAgentEligibility() {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/agent/eligibility`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

/**
 * Abort a running agent run.
 * @param {string} runId - Agent run UUID
 * @returns {Promise<{status: string}>}
 */
export async function abortAgentRun(runId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/agent/abort/${runId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

export async function getIdeationEvents(runId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/ideation/events/${runId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Support Chat
// ---------------------------------------------------------------------------

export async function startSupportChat() {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/support/chat/start`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

export async function sendSupportMessage(sessionId, content) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/support/chat/${sessionId}/message`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

export async function closeSupportChat(sessionId) {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/support/chat/${sessionId}/close`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

export async function getWinbackStatus() {
  const token = await getAuthToken({ retries: 3, retryDelayMs: 150 });
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/winback/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw createApiError(response, errorData);
  }
  return response.json();
}

// ============================================================================
// Referral API
// ============================================================================

export async function getReferralCode() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/referral/code`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function applyReferralCode(code) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/referral/apply`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function sendReferralInvite(email) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/referral/invite`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function getReferralStats() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/referral/stats`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

// ============================================================================
// Video Library (cross-project gallery) + Remix
// ============================================================================

/**
 * List every generated video across the user's projects, newest first.
 *
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=30]
 * @param {string} [params.search] - Matches prompt, video name, or project name
 * @param {boolean} [params.completedOnly] - Return only attachable completed videos
 * @returns {Promise<Object>} { success, videos, pagination }
 */
export async function listVideoLibrary({
  page = 1,
  limit = 30,
  search = null,
  completedOnly = false,
} = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const queryParams = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) queryParams.append('search', search);
  if (completedOnly) queryParams.append('completed_only', 'true');

  const response = await fetch(`${API_BASE_URL}/api/videos/library?${queryParams}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** List images across every project owned by the signed-in user. */
export async function listImageLibrary({ page = 1, limit = 100 } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const queryParams = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await fetch(`${API_BASE_URL}/api/images/library?${queryParams}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Fetch a single library video (used to poll remix/creation status).
 *
 * @param {string} videoId
 * @returns {Promise<Object>} { success, video }
 */
export async function getLibraryVideo(videoId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/videos/library/${videoId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/** Delete a standalone video-library item (one with no project). */
export async function deleteStandaloneVideo(videoId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/videos/library/${videoId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

/**
 * Remix an existing generated video with a new prompt (video-to-video).
 *
 * @param {string} videoId - Source video ID
 * @param {Object} params
 * @param {string} params.prompt - Editing instruction
 * @param {string} [params.model='gemini-omni-flash-edit'] - Video-to-video model alias
 * @param {string[]} [params.imageUrls] - Reference images (models that support them)
 * @param {string} [params.duration]
 * @param {string} [params.aspectRatio]
 * @param {string} [params.resolution]
 * @returns {Promise<Object>} { success, video, projectId }
 */
export async function remixVideo(videoId, params) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const { prompt, model, imageUrls, duration, aspectRatio, resolution, keepAudio } = params;

  const body = { prompt };
  if (model) body.model = model;
  if (imageUrls && imageUrls.length > 0) body.image_urls = imageUrls;
  if (duration) body.duration = duration;
  if (aspectRatio) body.aspect_ratio = aspectRatio;
  if (resolution) body.resolution = resolution;
  if (keepAudio !== undefined) body.keep_audio = keepAudio;

  const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}/remix`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('[remixVideo] request failed', {
      status: response.status,
      statusText: response.statusText,
      parsedError: error,
    });
    throw createApiError(response, error);
  }
  return response.json();
}

// ============================================================================
// Slack Agent (organization installation + per-user account linking)
// ============================================================================

export async function listSlackAgentInstallations() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/integrations/slack-agent/installations`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function createSlackAgentInstallUrl(redirectPath = '/settings/integrations') {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/integrations/slack-agent/install-url`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ redirect_path: redirectPath }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function revokeSlackAgentInstallation(installationId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(
    `${API_BASE_URL}/api/integrations/slack-agent/installations/${encodeURIComponent(installationId)}`,
    {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

export async function completeSlackAgentLink(linkToken) {
  const token = await getAuthToken({ retries: 10, retryDelayMs: 200 });
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/integrations/slack-agent/link/complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: linkToken }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.json();
}

// ============================================================================
// Social Accounts (Integrations — TikTok / Instagram publishing)
// ============================================================================

/**
 * List the current user's connected social accounts.
 * @param {'tiktok'|'instagram'} [platform] - Optional platform filter
 * @returns {Promise<{ success: boolean, accounts: Array<{ id: string, platform: string, username: string|null, user_id: string, profile_photo_url: string|null, status: 'connected'|'disconnected' }> }>}
 */
export async function getSocialAccounts(platform) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const params = new URLSearchParams();
  if (platform) params.append('platform', platform);
  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await fetch(`${API_BASE_URL}/api/social-accounts${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Reconcile our database with the provider and return the fresh account list.
 * Call after an OAuth connect flow completes and on manual refresh;
 * getSocialAccounts() reads our own database and never hits the provider.
 * @returns {Promise<{ success: boolean, accounts: Array }>}
 */
export async function syncSocialAccounts() {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/social-accounts/sync`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Create an OAuth URL for connecting a new social account.
 * Open the returned URL in a popup/tab; when the user completes the flow,
 * call syncSocialAccounts() to pull the new account into our database.
 * Can be called repeatedly to connect multiple accounts on the same platform.
 * @param {'tiktok'|'instagram'} platform
 * @returns {Promise<{ success: boolean, url: string, platform: string }>}
 */
export async function createSocialConnectUrl(platform) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/social-accounts/connect`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ platform }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Disconnect and remove a connected social account.
 * @param {string} accountId
 * @returns {Promise<{ success: boolean }>}
 */
export async function removeSocialAccount(accountId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/social-accounts/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

// ============================================================================
// Social Posts (publishing to connected accounts)
// ============================================================================

/**
 * Publish (or schedule / draft) a post to one or more connected accounts.
 * @param {Object} payload
 * @param {string[]} payload.socialAccountIds - Our social account ids (from getSocialAccounts)
 * @param {string} payload.caption
 * @param {string[]} payload.mediaUrls - Public video/image URLs
 * @param {string} [payload.scheduledAt] - ISO datetime to schedule instead of publishing now
 * @param {Object} [payload.platformConfigurations] - Per-platform settings (e.g. TikTok privacy)
 * @param {boolean} [payload.isDraft]
 * @returns {Promise<{ success: boolean, post: Object }>}
 */
export async function publishSocialPost(payload) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const body = {
    social_account_ids: payload.socialAccountIds,
    caption: payload.caption,
    media_urls: payload.mediaUrls,
  };
  if (payload.scheduledAt) body.scheduled_at = payload.scheduledAt;
  if (payload.platformConfigurations) body.platform_configurations = payload.platformConfigurations;
  if (payload.isDraft) body.is_draft = true;

  const response = await fetch(`${API_BASE_URL}/api/social-posts`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * List the current user's posts (our database).
 * @param {{ limit?: number, offset?: number }} [options]
 * @returns {Promise<{ success: boolean, posts: Array, total: number }>}
 */
export async function listSocialPosts({ limit = 50, offset = 0 } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/social-posts?limit=${limit}&offset=${offset}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Get one post with per-account publish results. Posts still in flight are
 * refreshed from the provider automatically; pass refresh=true to force it.
 * @param {string} postId
 * @param {{ refresh?: boolean }} [options]
 * @returns {Promise<{ success: boolean, post: Object }>}
 */
export async function getSocialPost(postId, { refresh = false } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const query = refresh ? '?refresh=true' : '';
  const response = await fetch(`${API_BASE_URL}/api/social-posts/${encodeURIComponent(postId)}${query}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Delete/cancel a post. Cancels scheduled/draft posts at the provider and
 * marks the post deleted in our database; already-live platform content is
 * not taken down.
 * @param {string} postId
 * @returns {Promise<{ success: boolean }>}
 */
export async function deleteSocialPost(postId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/social-posts/${encodeURIComponent(postId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

/**
 * Read a connected account's published posts from the platform.
 * Requires the account to have been connected with the "feeds" permission.
 * @param {string} accountId - Our social account id
 * @returns {Promise<{ success: boolean, feed: Object }>}
 */
export async function getSocialAccountFeed(accountId) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const response = await fetch(`${API_BASE_URL}/api/social-accounts/${encodeURIComponent(accountId)}/feed`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Workflow builder
// ---------------------------------------------------------------------------

async function workflowApi(path, { method = 'GET', body } = {}) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}/api${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined || isFormData ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: isFormData ? body : JSON.stringify(body) }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  return response.status === 204 ? null : response.json();
}

export const listWorkflows = () => workflowApi('/workflows');
export const getWorkflow = (id) => workflowApi(`/workflows/${encodeURIComponent(id)}`);
export const createWorkflow = (body) => workflowApi('/workflows', { method: 'POST', body });
export const updateWorkflowDraft = (id, body) => workflowApi(`/workflows/${encodeURIComponent(id)}/draft`, { method: 'PATCH', body });
export const validateWorkflow = (id, definition) => workflowApi(`/workflows/${encodeURIComponent(id)}/validate`, { method: 'POST', body: { definition } });
export const publishWorkflow = (id) => workflowApi(`/workflows/${encodeURIComponent(id)}/publish`, { method: 'POST' });
export const getWorkflowNodeTypes = () => workflowApi('/workflow-node-types');
export const getAgentDefinitions = () => workflowApi('/agent-definitions');

// Paid advertising integrations. These return canonical application ids;
// Zernio/Meta/TikTok identifiers never leak into saved workflow definitions.
export const listAdIntegrations = () => workflowApi('/ad-integrations');
export const beginAdIntegration = (platform, body = {}) => workflowApi(`/ad-integrations/connect/${encodeURIComponent(platform)}`, { method: 'POST', body });
export const getAdConnectAttempt = (id, stateToken) => workflowApi(`/ad-integrations/connect-attempts/${encodeURIComponent(id)}?${new URLSearchParams({ stateToken }).toString()}`);
export const syncAdIntegration = (id) => workflowApi(`/ad-integrations/${encodeURIComponent(id)}/sync`, { method: 'POST' });
export const listAdAccounts = (id) => workflowApi(`/ad-integrations/${encodeURIComponent(id)}/accounts`);
export const listAdResources = (id, params) => workflowApi(`/ad-integrations/${encodeURIComponent(id)}/resources?${new URLSearchParams(params).toString()}`);
export const removeAdIntegration = (id) => workflowApi(`/ad-integrations/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const searchAdTargeting = (connectionId, params) => workflowApi(`/ad-integrations/${encodeURIComponent(connectionId)}/targeting-search?${new URLSearchParams(params).toString()}`);
export const getAdPolicy = () => workflowApi('/ad-integrations/policy/me');
export const updateAdPolicy = (body) => workflowApi('/ad-integrations/policy/me', { method: 'PUT', body });
export const configureTikTokAdIdentity = (id, body) => workflowApi(`/ad-integrations/${encodeURIComponent(id)}/tiktok-brand-identity`, { method: 'PATCH', body });

export const getWorkflowAssistant = (id, threadId) => workflowApi(`/workflows/${encodeURIComponent(id)}/assistant${threadId ? `?thread_id=${encodeURIComponent(threadId)}` : ''}`);
export const createWorkflowAssistantSession = (id) => workflowApi(`/workflows/${encodeURIComponent(id)}/assistant/sessions`, { method: 'POST' });
export const renameWorkflowAssistantSession = (workflowId, threadId, title) => workflowApi(`/workflows/${encodeURIComponent(workflowId)}/assistant/sessions/${encodeURIComponent(threadId)}`, {
  method: 'PATCH',
  body: { title },
});
export const applyWorkflowAssistantProposal = (id, expectedRevision) => workflowApi(`/workflow-assistant-proposals/${encodeURIComponent(id)}/apply`, {
  method: 'POST',
  body: { expected_revision: expectedRevision },
});

export async function streamWorkflowAssistantMessage(id, body, onEvent, signal) {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');
  const response = await fetch(`${API_BASE_URL}/api/workflows/${encodeURIComponent(id)}/assistant/messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw createApiError(response, error);
  }
  if (!response.body) throw new Error('The assistant response stream is unavailable');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const dispatch = (block) => {
    const lines = block.split(/\r?\n/);
    const eventName = lines.find((line) => line.startsWith('event:'))?.slice(6).trim() || 'message';
    const data = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('\n');
    if (!data) return;
    const parsed = JSON.parse(data);
    onEvent?.({ ...parsed, type: parsed.type || eventName });
  };
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    let boundary = buffer.search(/\r?\n\r?\n/);
    while (boundary >= 0) {
      const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)?.[0] || '\n\n';
      dispatch(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + separator.length);
      boundary = buffer.search(/\r?\n\r?\n/);
    }
    if (done) break;
  }
  if (buffer.trim()) dispatch(buffer);
}

export const startWorkflowRun = (id, options = {}) => {
  const structured = options && (
    Object.hasOwn(options, 'input')
    || Object.hasOwn(options, 'attachments')
    || Object.hasOwn(options, 'attachmentReferences')
    || Object.hasOwn(options, 'inputFiles')
    || Object.hasOwn(options, 'allowUnattendedAdsActivation')
  );
  if (!structured) return workflowApi(`/workflows/${encodeURIComponent(id)}/runs`, { method: 'POST', body: { input: options } });
  const input = options.input || {};
  const attachments = options.attachments || [];
  const attachmentReferences = options.attachmentReferences || [];
  const inputFiles = options.inputFiles || {};
  const hasInputFiles = Object.values(inputFiles).some((files) => files?.length);
  const allowUnattendedAdsActivation = Boolean(options.allowUnattendedAdsActivation);
  if (!attachments.length && !attachmentReferences.length && !hasInputFiles) {
    return workflowApi(`/workflows/${encodeURIComponent(id)}/runs`, { method: 'POST', body: { input, allow_unattended_ads_activation: allowUnattendedAdsActivation } });
  }
  const body = new FormData();
  body.append('input', JSON.stringify(input));
  body.append('allow_unattended_ads_activation', String(allowUnattendedAdsActivation));
  attachments.forEach((file) => body.append('attachments', file));
  Object.entries(inputFiles).forEach(([field, files]) => {
    (files || []).forEach((file) => body.append(`input_file.${field}`, file));
  });
  if (attachmentReferences.length) body.append('attachment_references', JSON.stringify(attachmentReferences));
  return workflowApi(`/workflows/${encodeURIComponent(id)}/runs`, { method: 'POST', body });
};
export const startWorkflowNodeTest = (id, nodeId, body) => workflowApi(`/workflows/${encodeURIComponent(id)}/nodes/${encodeURIComponent(nodeId)}/test-runs`, { method: 'POST', body });
export const listWorkflowRuns = (id) => workflowApi(`/workflows/${encodeURIComponent(id)}/runs`);
export const listWorkflowNodeTestRuns = (id) => workflowApi(`/workflows/${encodeURIComponent(id)}/node-test-runs`);
export const getWorkflowRun = (id) => workflowApi(`/workflow-runs/${encodeURIComponent(id)}`);
export const getWorkflowRunEvents = (id, after = 0) => workflowApi(`/workflow-runs/${encodeURIComponent(id)}/events?after=${after}`);
export const cancelWorkflowRun = (id) => workflowApi(`/workflow-runs/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
export const retryWorkflowRun = (id) => workflowApi(`/workflow-runs/${encodeURIComponent(id)}/retry`, { method: 'POST' });

export const listWorkflowTriggers = (id) => workflowApi(`/workflows/${encodeURIComponent(id)}/triggers`);
export const createWorkflowTrigger = (id, body) => workflowApi(`/workflows/${encodeURIComponent(id)}/triggers`, { method: 'POST', body });
export const updateWorkflowTrigger = (id, body) => workflowApi(`/workflow-triggers/${encodeURIComponent(id)}`, { method: 'PATCH', body });
export const pauseWorkflowTrigger = (id) => workflowApi(`/workflow-triggers/${encodeURIComponent(id)}/pause`, { method: 'POST' });
export const resumeWorkflowTrigger = (id) => workflowApi(`/workflow-triggers/${encodeURIComponent(id)}/resume`, { method: 'POST' });
export const deleteWorkflowTrigger = (id) => workflowApi(`/workflow-triggers/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const listWorkflowApprovals = (status = 'pending') => workflowApi(`/workflow-approvals?status=${encodeURIComponent(status)}`);
export const resolveWorkflowApproval = (id, approved, expectedVersion, comment = '') => workflowApi(`/workflow-approvals/${encodeURIComponent(id)}/${approved ? 'approve' : 'reject'}`, {
  method: 'POST',
  body: { expected_version: expectedVersion, comment },
});

export { API_BASE_URL };
